import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Button, CircularProgress, Box, Typography, Paper, TextField, Alert,
    Tabs, Tab, IconButton, Autocomplete, Chip, Divider, Grid, Tooltip, Badge,
    InputAdornment,
    MenuItem, Checkbox, ToggleButton, ToggleButtonGroup, useMediaQuery,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PushPinIcon from '@mui/icons-material/PushPin';
import SaveIcon from '@mui/icons-material/Save';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import axios from 'axios';
import axiosAPI from 'Includes/api/api';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import { bindKarnatakaAutocomplete, geocodePlace, loadGoogleMaps, reverseGeocode, watchLiveLocation } from 'Includes/googleMaps';
import { useNavigate } from 'react-router-dom';
import { useSignupAdmin } from 'Includes/signupAdmin';

// ── Constants ─────────────────────────────────────────────────────────────
// Use axiosAPI baseURL (port 8002) — hardcoded :8000 breaks local find-schools
const SUGGEST_PATH = '/api/area-search/suggest/';
const NEARBY_PATH = '/api/area-search/nearby/';
const BULK_SAVE_PATH = '/api/area-search/bulk-save/';
const STORED_PATH = '/api/area-search/stored/';
const RESOLVE_MAP_LINK_PATH = '/api/area-search/resolve-map-link/';
const DEFAULT_CENTER = { lat: 12.9716, lng: 77.5946 };
const MAX_SELECT = 6;

const VISIT_TYPES = [
    { value: 'sales', label: 'Sales' },
    { value: 'support', label: 'Support' },
    { value: 'sales_support', label: 'Sales & Support' },
];

const TYPE_COLORS = { school: '#1976d2', college: '#7b1fa2', university: '#7b1fa2', client: '#2e7d32' };
const TYPE_LABELS = { school: 'School', college: 'College', university: 'University', client: 'Our Client' };
const RADIUS_OPTIONS = [1, 3, 5, 10, 15];

const MAP_COORD_PATTERNS = [
    /!3d([+-]?\d+\.?\d*)!4d([+-]?\d+\.?\d*)/,
    /[?&](?:q|query|ll)=([+-]?\d+\.?\d*),\s*([+-]?\d+\.?\d*)/,
    /(?:\/search\/|\/place\/|\/maps\/)([+-]?\d+\.?\d*),([+-]?\d+\.?\d*)/,
    /@([+-]?\d+\.?\d*),([+-]?\d+\.?\d*)/,
];

const extractMapCoordinates = (input) => {
    const text = (input || '').trim();
    if (!text) return null;
    for (const pattern of MAP_COORD_PATTERNS) {
        const match = text.match(pattern);
        if (!match) continue;
        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);
        if (Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            return { lat, lng };
        }
    }
    return null;
};

const placeNameFromMapUrl = (input) => {
    const match = (input || '').match(/\/place\/([^/@?#]+)/);
    if (!match) return '';
    try {
        const name = decodeURIComponent(match[1]).replace(/\+/g, ' ').trim();
        if (/^[+-]?\d+\.?\d*\s*,\s*[+-]?\d+\.?\d*$/.test(name)) return '';
        return name;
    } catch {
        return '';
    }
};

const isGoogleMapLink = (input) => /google\.[a-z.]+\/maps|maps\.app\.goo\.gl|goo\.gl\/maps/i.test(input || '');

const stopKey = (s) => {
    if (s?.id != null) return `id:${s.id}`;
    return `geo:${Number(s?.lat).toFixed(5)},${Number(s?.lng).toFixed(5)}:${(s?.name || '').trim().toLowerCase()}`;
};

const includesSupport = (visitType) => visitType === 'support' || visitType === 'sales_support';
const isSalesAndSupport = (visitType) => visitType === 'sales_support';
const visitTypeLabel = (visitType) =>
    VISIT_TYPES.find((t) => t.value === visitType)?.label || 'Sales';

/** Our-client schools first, then other schools — used for Sales & Support planning. */
const prioritizeOurSchools = (schools) =>
    [...schools].sort((a, b) => {
        const aOurs = a?.is_client ? 0 : 1;
        const bOurs = b?.is_client ? 0 : 1;
        if (aOurs !== bOurs) return aOurs - bOurs;
        return 0;
    });

const PIN_CLASS = 'visit-planner-pin';

const toRad = (deg) => (deg * Math.PI) / 180;

const haversineKm = (a, b) => {
    if (a?.lat == null || a?.lng == null || b?.lat == null || b?.lng == null) return Infinity;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)));
};

const TILE_RADIUS_KM = 5;

/** Point `distanceKm` away from origin. 0° is north, clockwise. */
const offsetKm = (origin, distanceKm, bearing) => {
    const br = toRad(bearing);
    const lat = Number(origin.lat) + (distanceKm / 111) * Math.cos(br);
    const lng = Number(origin.lng) + (distanceKm / (111 * Math.cos(toRad(origin.lat)))) * Math.sin(br);
    return { lat, lng };
};

/**
 * 1 km, 3 km, and 5 km stay one request. 10 km and 15 km are rings of 5 km searches
 * so each request stays the size that already finishes in production.
 * Middle ring: 6 centres, 8.6 km out (meets the 10 km rim).
 * Outer ring: 12 centres, 12 km out (meets the 15 km rim).
 */
const radiusSearchTiles = (origin, radiusKm) => {
    if (!origin || radiusKm <= TILE_RADIUS_KM) {
        return [{ center: origin, radiusKm, ring: 'centre' }];
    }
    const tiles = [{ center: origin, radiusKm: TILE_RADIUS_KM, ring: 'centre' }];
    if (radiusKm >= 10) {
        for (let i = 0; i < 6; i += 1) {
            tiles.push({ center: offsetKm(origin, 8.6, i * 60), radiusKm: TILE_RADIUS_KM, ring: 'middle' });
        }
    }
    if (radiusKm >= 15) {
        for (let i = 0; i < 12; i += 1) {
            tiles.push({ center: offsetKm(origin, 12, i * 30), radiusKm: TILE_RADIUS_KM, ring: 'outer' });
        }
    }
    return tiles;
};

const GOVT_NAME_RE = /government|\bgovt\b|kendriya vidyalaya|jawahar navodaya|navodaya vidyalaya|\b(?:ghps|ghs|guhps|gmhps|gpuc|gjc|gghs|gbhs)\b/i;

/** Private and aided only. Aided stays even when the name also says government. */
const isPrivateOrAided = (school) => {
    const text = `${school?.name || ''} ${school?.school_type || ''} ${school?.management || ''}`.toLowerCase();
    if (text.includes('aided')) return true;
    return !GOVT_NAME_RE.test(text);
};

const schoolDedupeKey = (school) => {
    const lat = Number(school?.lat);
    const lng = Number(school?.lng);
    const name = (school?.name || '').trim().toLowerCase();
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return `name:${name}`;
    return `${lat.toFixed(5)},${lng.toFixed(5)},${name}`;
};

const ringLabel = (ring) => {
    if (ring === 'middle') return 'middle 5 km';
    if (ring === 'outer') return 'outer 5 km';
    return 'inner 5 km';
};

const pointOf = (school) => ({ lat: Number(school.lat), lng: Number(school.lng) });

const centroidOf = (schools) => {
    const n = schools.length || 1;
    return {
        lat: schools.reduce((sum, school) => sum + Number(school.lat), 0) / n,
        lng: schools.reduce((sum, school) => sum + Number(school.lng), 0) / n,
    };
};

/** Covering radius: farthest school from the group's centre. Smaller means a tighter day. */
const coveringRadiusKm = (schools) => {
    if (!schools.length) return Infinity;
    const centre = centroidOf(schools);
    return schools.reduce(
        (max, school) => Math.max(max, haversineKm(centre, pointOf(school))),
        0
    );
};

/**
 * Walk the day's stops in a short path.
 * Begin at the stop closest to the anchor, then always go to the nearest stop still left.
 */
const orderNearestNeighbor = (schools, anchor) => {
    const pool = [...schools];
    const ordered = [];
    let cursor = anchor?.lat != null && anchor?.lng != null ? anchor : null;
    while (pool.length) {
        let bestIdx = 0;
        let bestDist = Infinity;
        pool.forEach((school, idx) => {
            const dist = cursor ? haversineKm(cursor, pointOf(school)) : 0;
            if (dist < bestDist) {
                bestDist = dist;
                bestIdx = idx;
            }
        });
        const next = pool.splice(bestIdx, 1)[0];
        ordered.push(next);
        cursor = pointOf(next);
    }
    return ordered;
};

/**
 * One day = one tight pocket of nearby schools, including schools in other compass sectors.
 * The first pocket is the densest group near the start pin. The next day starts beside
 * that pocket and again takes the smallest nearby group, so travel stays short.
 */
const planDaysByProximity = (schools, anchor, perDay, preferClients = false) => {
    const remaining = (schools || []).filter((school) => school?.lat != null && school?.lng != null);
    const unlocated = (schools || []).filter((school) => school?.lat == null || school?.lng == null);
    const days = [];
    let currentAnchor = anchor?.lat != null && anchor?.lng != null ? anchor : null;
    const daySize = Math.max(1, perDay);

    while (remaining.length) {
        const take = Math.min(daySize, remaining.length);
        const seedWindow = Math.max(take * 6, 30);
        const seeds = currentAnchor
            ? [...remaining]
                .sort((a, b) => haversineKm(currentAnchor, pointOf(a)) - haversineKm(currentAnchor, pointOf(b)))
                .slice(0, seedWindow)
            : remaining;

        let best = null;
        seeds.forEach((seed) => {
            const seedPoint = pointOf(seed);
            const pocket = [...remaining]
                .sort((a, b) => haversineKm(seedPoint, pointOf(a)) - haversineKm(seedPoint, pointOf(b)))
                .slice(0, take);
            const radius = coveringRadiusKm(pocket);
            const centre = centroidOf(pocket);
            const anchorDist = currentAnchor ? haversineKm(currentAnchor, centre) : 0;
            // Radius dominates so a tight cross-sector group beats a long radial sector.
            // Anchor distance keeps the plan moving out from the start instead of jumping across the city.
            let score = radius + anchorDist * 0.35;
            if (preferClients) {
                const clientCount = pocket.filter((school) => school.is_client).length;
                score -= clientCount * 0.15;
            }
            if (!best || score < best.score) best = { pocket, score };
        });

        const ordered = orderNearestNeighbor(best.pocket, currentAnchor);
        days.push(ordered);
        const chosen = new Set(best.pocket.map(stopKey));
        for (let i = remaining.length - 1; i >= 0; i -= 1) {
            if (chosen.has(stopKey(remaining[i]))) remaining.splice(i, 1);
        }
        currentAnchor = pointOf(ordered[ordered.length - 1]);
    }

    for (let i = 0; i < unlocated.length; i += daySize) {
        days.push(unlocated.slice(i, i + daySize));
    }
    return days;
};

const isNotInterestedSchool = (school) =>
    Boolean(school?.not_interested || school?.interest_status === 'not_interested' || school?.last_interest_status === 'not_interested');

const INTEREST_LABELS = {
    interested: 'Interested',
    not_interested: 'Not interested',
    pending: 'Pending',
    followup: 'Follow-up',
    go_to_main_branch: 'Go to main branch',
};

const interestChipSx = (status) => {
    if (status === 'interested') return { bgcolor: '#d1fae5', color: '#047857' };
    if (status === 'not_interested') return { bgcolor: '#fee2e2', color: '#b91c1c' };
    if (status === 'followup') return { bgcolor: '#fef3c7', color: '#b45309' };
    if (status === 'go_to_main_branch') return { bgcolor: '#e0e7ff', color: '#3730a3' };
    return { bgcolor: '#f1f5f9', color: '#475569' };
};

const schoolVisitHistory = (school) => {
    const count = Number(school?.visit_count) || 0;
    if (count <= 0) return null;
    const status = school.last_interest_status || school.interest_status || 'pending';
    return {
        count,
        status,
        label: INTEREST_LABELS[status] || String(status).replace(/_/g, ' '),
    };
};

const VisitHistoryLine = ({ school }) => {
    const history = schoolVisitHistory(school);
    if (!history) return null;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.4 }}>
            <Chip
                size="small"
                label={`${history.count} visit${history.count === 1 ? '' : 's'}`}
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#e0f2fe', color: '#0369a1' }}
            />
            <Chip
                size="small"
                label={`Last: ${history.label}`}
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, ...interestChipSx(history.status) }}
            />
        </Box>
    );
};

// ── Helpers ────────────────────────────────────────────────────────────────
const datesBetween = (from, to) => {
    const dates = [];
    const cur = new Date(from);
    const end = new Date(to);
    while (cur <= end) {
        if (cur.getDay() !== 0) {
            dates.push(cur.toISOString().slice(0, 10));
        }
        cur.setDate(cur.getDate() + 1);
    }
    return dates;
};

const fmt = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', weekday: 'short' });
};

// Mock time generator for UI
const generateTime = (idx) => {
    const baseHour = 9;
    const totalMinutes = baseHour * 60 + (idx * 75); // 1 hr 15 mins between visits
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayH = h > 12 ? h - 12 : (h === 0 ? 12 : h);
    return `${displayH.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')} ${ampm}`;
};

// ── Map Component (Google Maps) ────────────────────────────────────────────
const InstitutePickMap = ({ open, location, fallback, onPick }) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerRef = useRef(null);
    const onPickRef = useRef(onPick);
    const [mapReady, setMapReady] = useState(false);

    useEffect(() => { onPickRef.current = onPick; }, [onPick]);

    useEffect(() => {
        if (!open || !mapRef.current) return undefined;
        let cancelled = false;
        loadGoogleMaps().then((maps) => {
            if (cancelled || !mapRef.current) return;
            const centre = location?.lat != null ? location : fallback;
            const map = new maps.Map(mapRef.current, {
                center: {
                    lat: Number(centre?.lat ?? DEFAULT_CENTER.lat),
                    lng: Number(centre?.lng ?? DEFAULT_CENTER.lng),
                },
                zoom: location?.lat != null ? 16 : 13,
                mapTypeControl: false,
                streetViewControl: false,
                gestureHandling: 'greedy',
            });
            mapInstanceRef.current = map;
            setMapReady(true);
            map.addListener('click', (event) => {
                onPickRef.current?.({ lat: event.latLng.lat(), lng: event.latLng.lng() });
            });
        }).catch(() => {});
        return () => {
            cancelled = true;
            mapInstanceRef.current = null;
            markerRef.current = null;
            setMapReady(false);
        };
    }, [open]);

    useEffect(() => {
        const map = mapInstanceRef.current;
        const maps = window.google?.maps;
        if (!map || !maps || !open || !mapReady) return;
        if (markerRef.current) markerRef.current.setMap(null);
        if (location?.lat == null || location?.lng == null) return;
        const position = { lat: Number(location.lat), lng: Number(location.lng) };
        markerRef.current = new maps.Marker({ map, position, draggable: true });
        markerRef.current.addListener('dragend', (event) => {
            onPickRef.current?.({ lat: event.latLng.lat(), lng: event.latLng.lng() });
        });
        map.panTo(position);
        if ((map.getZoom() || 0) < 16) map.setZoom(16);
    }, [open, location, mapReady]);

    if (!open) return null;

    return (
        <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
                Click the map or drag the pin to set the institute location.
            </Typography>
            <Box
                ref={mapRef}
                sx={{ mt: 0.5, height: 260, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}
            />
        </Box>
    );
};

const RouteMap = ({
    route, startLoc, areaCentre, setStartLoc, locationPin, setLocationPin,
    foundSchools = [], radiusKm = 1, pinMode = '', resizeKey, focusTarget,
}) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const schoolMarkersRef = useRef([]);
    const routeLineRef = useRef(null);
    const startMarkerRef = useRef(null);
    const locationMarkerRef = useRef(null);
    const radiusCircleRef = useRef(null);
    const pinModeRef = useRef(pinMode);
    const setStartLocRef = useRef(setStartLoc);
    const setLocationPinRef = useRef(setLocationPin);
    const allowAutoZoomRef = useRef(true);
    const lastAutoZoomKeyRef = useRef('');
    const startFollowKeyRef = useRef('');
    const mapHasPlacesRef = useRef(false);
    const consumedFocusRef = useRef(null);
    const liveMarkerRef = useRef(null);
    const [mapReady, setMapReady] = useState(false);

    useEffect(() => { pinModeRef.current = pinMode; }, [pinMode]);
    useEffect(() => { setStartLocRef.current = setStartLoc; }, [setStartLoc]);
    useEffect(() => { setLocationPinRef.current = setLocationPin; }, [setLocationPin]);

    useEffect(() => {
        if (!mapRef.current || mapInstanceRef.current) return undefined;
        let cancelled = false;
        loadGoogleMaps().then((maps) => {
            if (cancelled || !mapRef.current || mapInstanceRef.current) return;
            const map = new maps.Map(mapRef.current, {
                center: { lat: startLoc.lat, lng: startLoc.lng },
                zoom: 13,
                mapTypeControl: true,
                streetViewControl: false,
                fullscreenControl: false,
                gestureHandling: 'greedy',
            });
            mapInstanceRef.current = map;
            map.addListener('click', (event) => {
                const mode = pinModeRef.current;
                const point = { lat: event.latLng.lat(), lng: event.latLng.lng() };
                if (mode === 'start' && setStartLocRef.current) setStartLocRef.current(point);
                if (mode === 'location' && setLocationPinRef.current) setLocationPinRef.current(point);
            });
            map.addListener('dragstart', () => { allowAutoZoomRef.current = false; });
            map.addListener('zoom_changed', () => {
                if (map.get('userZoom')) allowAutoZoomRef.current = false;
            });
            setMapReady(true);
        }).catch(() => {});
        return () => {
            cancelled = true;
            mapInstanceRef.current = null;
            liveMarkerRef.current = null;
            setMapReady(false);
        };
    }, []);

    useEffect(() => {
        const map = mapInstanceRef.current;
        const maps = window.google?.maps;
        if (!map || !maps || !mapReady) return undefined;
        return watchLiveLocation(map, maps, liveMarkerRef);
    }, [mapReady]);

    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map || !window.google?.maps) return undefined;
        const t = setTimeout(() => window.google.maps.event.trigger(map, 'resize'), 120);
        return () => clearTimeout(t);
    }, [resizeKey, mapReady]);

    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map || focusTarget?.lat == null || focusTarget?.lng == null) return;
        map.panTo({ lat: Number(focusTarget.lat), lng: Number(focusTarget.lng) });
        if ((map.getZoom() || 0) < 15) map.setZoom(15);
    }, [focusTarget, mapReady]);

    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map) return;
        map.setOptions({ draggableCursor: pinMode ? 'crosshair' : null });
    }, [pinMode, mapReady]);

    useEffect(() => {
        mapHasPlacesRef.current = (route && route.length > 0)
            || foundSchools.length > 0
            || Boolean(locationPin?.lat)
            || Boolean(areaCentre?.lat);
    }, [route, foundSchools, locationPin, areaCentre]);

    useEffect(() => {
        const map = mapInstanceRef.current;
        const maps = window.google?.maps;
        if (!map || !maps || startLoc?.lat == null || startLoc?.lng == null) return;
        if (startMarkerRef.current) startMarkerRef.current.setMap(null);
        startMarkerRef.current = new maps.Marker({
            map,
            position: { lat: Number(startLoc.lat), lng: Number(startLoc.lng) },
            draggable: true,
            zIndex: 1000,
            label: { text: 'S', color: '#fff', fontWeight: '700' },
            title: 'Start pin',
        });
        startMarkerRef.current.addListener('dragend', (event) => {
            if (setStartLocRef.current) {
                setStartLocRef.current({ lat: event.latLng.lat(), lng: event.latLng.lng() });
            }
        });
        const followKey = `${startLoc.lat},${startLoc.lng}`;
        if (!mapHasPlacesRef.current && startFollowKeyRef.current !== followKey) {
            startFollowKeyRef.current = followKey;
            map.panTo({ lat: Number(startLoc.lat), lng: Number(startLoc.lng) });
        }
    }, [startLoc, mapReady]);

    useEffect(() => {
        const map = mapInstanceRef.current;
        const maps = window.google?.maps;
        if (!map || !maps) return;
        if (locationMarkerRef.current) {
            locationMarkerRef.current.setMap(null);
            locationMarkerRef.current = null;
        }
        if (radiusCircleRef.current) {
            radiusCircleRef.current.setMap(null);
            radiusCircleRef.current = null;
        }
        const centre = locationPin || areaCentre;
        if (!centre?.lat || !centre?.lng) return;
        locationMarkerRef.current = new maps.Marker({
            map,
            position: { lat: Number(centre.lat), lng: Number(centre.lng) },
            draggable: true,
            zIndex: 900,
            label: { text: 'L', color: '#fff', fontWeight: '700' },
            title: `Location pin · within ${radiusKm} km`,
        });
        locationMarkerRef.current.addListener('dragend', (event) => {
            if (setLocationPinRef.current) {
                setLocationPinRef.current({ lat: event.latLng.lat(), lng: event.latLng.lng() });
            }
        });
        const pinPoint = { lat: Number(centre.lat), lng: Number(centre.lng) };
        const showPin = () => {
            map.setCenter(pinPoint);
            if ((map.getZoom() || 0) < 14) map.setZoom(15);
        };
        showPin();
        const recenterTimer = setTimeout(showPin, 300);
        radiusCircleRef.current = new maps.Circle({
            map,
            center: { lat: Number(centre.lat), lng: Number(centre.lng) },
            radius: radiusKm * 1000,
            strokeColor: '#e65100',
            strokeWeight: 2,
            fillColor: '#fb8c00',
            fillOpacity: 0.08,
        });
        return () => clearTimeout(recenterTimer);
    }, [locationPin, areaCentre, radiusKm, mapReady]);

    useEffect(() => {
        const map = mapInstanceRef.current;
        const maps = window.google?.maps;
        if (!map || !maps) return;
        schoolMarkersRef.current.forEach((marker) => marker.setMap(null));
        schoolMarkersRef.current = [];
        if (routeLineRef.current) {
            routeLineRef.current.setMap(null);
            routeLineRef.current = null;
        }

        // Prefer planned day route; otherwise show all found schools from search.
        // Manual institutes stay on the map even when a day route is showing.
        const routeKeys = new Set((route || []).map((s) => stopKey(s)));
        const extraManual = route.length > 0
            ? foundSchools.filter((s) => s.manual && !routeKeys.has(stopKey(s)) && !isNotInterestedSchool(s))
            : [];
        const markers = route.length > 0 ? [...route, ...extraManual] : foundSchools;
        const excludedMarkers = route.length > 0
            ? foundSchools.filter(isNotInterestedSchool)
            : [];

        const autoZoomKey = [
            route.length,
            route.map((s) => `${s.id || s.name}:${s.lat},${s.lng}`).join('|'),
            foundSchools.length,
            foundSchools.map((s) => `${s.id || s.name}:${s.lat},${s.lng}`).join('|'),
            areaCentre ? `${areaCentre.lat},${areaCentre.lng}` : '',
            locationPin ? `${locationPin.lat},${locationPin.lng}` : '',
        ].join('::');

        const keyChanged = autoZoomKey !== lastAutoZoomKeyRef.current;
        if (keyChanged) {
            lastAutoZoomKeyRef.current = autoZoomKey;
            allowAutoZoomRef.current = true;
        }

        // A picked search area should centre the map on that point. Consume the
        // focus once so a later school search can still fit the results.
        const focusToken = focusTarget?.token;
        const honourFocus = focusToken != null
            && consumedFocusRef.current !== focusToken
            && locationPin?.lat != null
            && locationPin?.lng != null
            && Number(locationPin.lat) === Number(focusTarget.lat)
            && Number(locationPin.lng) === Number(focusTarget.lng);
        if (honourFocus) consumedFocusRef.current = focusToken;

        const fitGoogle = (points) => {
            if (honourFocus) return;
            if (!keyChanged || !allowAutoZoomRef.current || !points.length) return;
            if (points.length === 1) {
                map.panTo(points[0]);
                map.setZoom(13);
                return;
            }
            const bounds = new maps.LatLngBounds();
            points.forEach((point) => bounds.extend(point));
            map.fitBounds(bounds, 64);
        };

        if (markers.length === 0 && excludedMarkers.length === 0) {
            const centre = locationPin || areaCentre;
            const points = [];
            if (centre) points.push({ lat: Number(centre.lat), lng: Number(centre.lng) });
            if (startLoc && (!centre || haversineKm(centre, startLoc) <= Math.max(radiusKm * 4, 30))) {
                points.push({ lat: Number(startLoc.lat), lng: Number(startLoc.lng) });
            }
            fitGoogle(points);
            if (honourFocus) {
                map.setCenter({ lat: Number(focusTarget.lat), lng: Number(focusTarget.lng) });
                map.setZoom(15);
            }
            return;
        }

        const routePath = route.length > 0 && startLoc
            ? [{ lat: Number(startLoc.lat), lng: Number(startLoc.lng) }]
            : [];
        const allPoints = [];
        let planNumber = 0;
        const info = new maps.InfoWindow();

        const addMarker = (school, notInterested) => {
            if (school.lat == null || school.lng == null) return;
            const point = { lat: Number(school.lat), lng: Number(school.lng) };
            allPoints.push(point);
            const labelNum = notInterested ? '!' : String(++planNumber);
            if (!notInterested && route.length > 0) routePath.push(point);
            const history = schoolVisitHistory(school);
            const marker = new maps.Marker({
                map,
                position: point,
                label: { text: labelNum, color: '#fff', fontSize: '12px', fontWeight: '700' },
                title: school.name || '',
            });
            marker.addListener('click', () => {
                const historyText = history
                    ? `<br><small>${history.count} visit${history.count === 1 ? '' : 's'} · last interest: ${history.label}</small>`
                    : '';
                info.setContent(
                    `<b>${school.name || ''}</b><br><small>${school.address || ''}</small>${historyText}`
                );
                info.open({ map, anchor: marker });
            });
            schoolMarkersRef.current.push(marker);
        };

        markers.forEach((school) => addMarker(school, isNotInterestedSchool(school)));
        excludedMarkers.forEach((school) => addMarker(school, true));

        if (routePath.length > 1) {
            routeLineRef.current = new maps.Polyline({
                map,
                path: routePath,
                strokeColor: '#1976d2',
                strokeWeight: 3,
                strokeOpacity: 0.85,
            });
        }
        const fitPoints = [...allPoints];
        const centre = locationPin || areaCentre;
        const startNear = startLoc && (
            route.length > 0
            || !centre
            || haversineKm(centre, startLoc) <= Math.max(radiusKm * 4, 30)
            || (allPoints.length > 0 && haversineKm(allPoints[0], startLoc) <= Math.max(radiusKm * 4, 30))
        );
        if (startNear) fitPoints.push({ lat: Number(startLoc.lat), lng: Number(startLoc.lng) });
        if (centre?.lat != null && allPoints.length === 0) {
            fitPoints.push({ lat: Number(centre.lat), lng: Number(centre.lng) });
        }
        fitGoogle(fitPoints.length ? fitPoints : (startLoc ? [{ lat: Number(startLoc.lat), lng: Number(startLoc.lng) }] : []));
        if (honourFocus) {
            map.setCenter({ lat: Number(focusTarget.lat), lng: Number(focusTarget.lng) });
            map.setZoom(15);
        }
    }, [route, startLoc, foundSchools, areaCentre, locationPin, radiusKm, focusTarget, mapReady]);

    return (
        <>
            <style>{`.${PIN_CLASS}{background:transparent !important;border:none !important;}`}</style>
            <Box ref={mapRef} sx={{ width: '100%', height: '100%' }} />
        </>
    );
};

const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const GoogleInstituteSearch = ({ open, onPlace, getOrigin }) => {
    const inputRef = useRef(null);
    const onPlaceRef = useRef(onPlace);
    const getOriginRef = useRef(getOrigin);
    useEffect(() => { onPlaceRef.current = onPlace; }, [onPlace]);
    useEffect(() => { getOriginRef.current = getOrigin; }, [getOrigin]);
    useEffect(() => {
        if (!open) {
            if (inputRef.current) inputRef.current.value = '';
            return undefined;
        }
        let cancelled = false;
        loadGoogleMaps().then((maps) => {
            if (cancelled || !inputRef.current) return;
            bindKarnatakaAutocomplete(maps, inputRef.current, (place) => {
                onPlaceRef.current(place);
            }, () => getOriginRef.current?.());
        }).catch(() => {});
        return () => {
            cancelled = true;
            inputRef.current?._karnatakaCleanup?.();
        };
    }, [open]);
    return (
        <>
            <style>{`.pac-container{z-index:3000 !important;}`}</style>
            <TextField
                inputRef={inputRef}
                margin="dense"
                label="Search Google Maps"
                placeholder="Private schools, colleges, or locations in Karnataka"
                fullWidth
                size="small"
                sx={{ mt: 1 }}
            />
        </>
    );
};

const GooglePlanMap = ({
    center, schools = [], plannedStopKeys, route = [], startLoc, onStartChange,
    onPlaceSelected, onAddToPlan, onCreateSchool, onLocate, busy,
}) => {
    const mapNodeRef = useRef(null);
    const searchRef = useRef(null);
    const mapRef = useRef(null);
    const searchMarkerRef = useRef(null);
    const draftMarkerRef = useRef(null);
    const infoRef = useRef(null);
    const schoolMarkersRef = useRef([]);
    const startMarkerRef = useRef(null);
    const routeLineRef = useRef(null);
    const onPlaceSelectedRef = useRef(onPlaceSelected);
    const onAddToPlanRef = useRef(onAddToPlan);
    const onCreateSchoolRef = useRef(onCreateSchool);
    const onStartChangeRef = useRef(onStartChange);
    const onLocateRef = useRef(onLocate);
    const hereMarkerRef = useRef(null);
    const [mapError, setMapError] = useState('');
    const [mapReady, setMapReady] = useState(false);
    const [locating, setLocating] = useState(false);

    useEffect(() => { onPlaceSelectedRef.current = onPlaceSelected; }, [onPlaceSelected]);
    useEffect(() => { onAddToPlanRef.current = onAddToPlan; }, [onAddToPlan]);
    useEffect(() => { onCreateSchoolRef.current = onCreateSchool; }, [onCreateSchool]);
    useEffect(() => { onStartChangeRef.current = onStartChange; }, [onStartChange]);
    useEffect(() => { onLocateRef.current = onLocate; }, [onLocate]);

    useEffect(() => {
        let cancelled = false;
        loadGoogleMaps()
            .then((maps) => {
                if (cancelled || !mapNodeRef.current || mapRef.current) return;
                const map = new maps.Map(mapNodeRef.current, {
                    center: center?.lat != null ? center : DEFAULT_CENTER,
                    zoom: 14,
                    mapTypeControl: true,
                    streetViewControl: false,
                    fullscreenControl: true,
                    gestureHandling: 'greedy',
                });
                mapRef.current = map;
                infoRef.current = new maps.InfoWindow();
                bindKarnatakaAutocomplete(maps, searchRef.current, (place) => {
                    const point = { lat: place.lat, lng: place.lng };
                    map.panTo(point);
                    if ((map.getZoom() || 0) < 15) map.setZoom(15);
                    if (searchMarkerRef.current) searchMarkerRef.current.setMap(null);
                    searchMarkerRef.current = new maps.Marker({
                        map,
                        position: point,
                        title: place.name || place.address || 'Searched location',
                    });
                    const label = place.address || place.name || '';
                    onPlaceSelectedRef.current?.(label, point);
                }, () => {
                    const mapCenter = mapRef.current?.getCenter?.();
                    if (!mapCenter) return null;
                    return { lat: mapCenter.lat(), lng: mapCenter.lng() };
                });
                map.addListener('click', (event) => {
                    const point = { lat: event.latLng.lat(), lng: event.latLng.lng() };
                    if (draftMarkerRef.current) draftMarkerRef.current.setMap(null);
                    const marker = new maps.Marker({ map, position: point, draggable: true });
                    draftMarkerRef.current = marker;
                    const openDraft = (latLng) => {
                        const html = `
                            <div style="font-family:Roboto,Arial,sans-serif;min-width:200px">
                                <div style="font-weight:600;margin-bottom:6px">Add a school here</div>
                                <input id="gp-school-name" placeholder="School name" style="width:100%;box-sizing:border-box;padding:8px;border:1px solid #dadce0;border-radius:4px;font-size:14px" />
                                <button id="gp-school-add" type="button" style="margin-top:8px;background:#1a73e8;color:#fff;border:0;border-radius:4px;padding:8px 12px;font-weight:600;cursor:pointer">Select</button>
                            </div>`;
                        infoRef.current.setContent(html);
                        infoRef.current.open({ map, anchor: marker });
                        maps.event.addListenerOnce(infoRef.current, 'domready', () => {
                            const input = document.getElementById('gp-school-name');
                            const button = document.getElementById('gp-school-add');
                            input?.focus();
                            const submit = () => {
                                const name = input?.value?.trim();
                                if (!name) {
                                    input?.focus();
                                    return;
                                }
                                const position = marker.getPosition();
                                Promise.resolve(onCreateSchoolRef.current?.({
                                    name,
                                    lat: position.lat(),
                                    lng: position.lng(),
                                    type: 'school',
                                    address: '',
                                })).then((saved) => {
                                    if (!saved) return;
                                    infoRef.current?.close();
                                    marker.setMap(null);
                                    if (draftMarkerRef.current === marker) draftMarkerRef.current = null;
                                });
                            };
                            button?.addEventListener('click', submit);
                            input?.addEventListener('keydown', (keyEvent) => {
                                if (keyEvent.key === 'Enter') {
                                    keyEvent.preventDefault();
                                    submit();
                                }
                            });
                        });
                    };
                    openDraft(point);
                    marker.addListener('dragend', () => openDraft(marker.getPosition()));
                });
                setMapReady(true);
            })
            .catch((err) => {
                if (!cancelled) setMapError(err.message || 'Could not load Google Maps.');
            });
        return () => {
            cancelled = true;
            searchRef.current?._karnatakaCleanup?.();
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        const maps = window.google?.maps;
        if (!map || !maps || !mapReady) return undefined;
        schoolMarkersRef.current.forEach((marker) => marker.setMap(null));
        schoolMarkersRef.current = [];
        schools.forEach((school) => {
            const lat = Number(school.lat);
            const lng = Number(school.lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
            const position = { lat, lng };
            const onPlan = plannedStopKeys?.has?.(stopKey(school));
            const blocked = isNotInterestedSchool(school);
            const marker = new maps.Marker({
                map,
                position,
                title: school.name,
                icon: blocked
                    ? undefined
                    : (onPlan
                        ? 'https://maps.google.com/mapfiles/ms/icons/green-dot.png'
                        : 'https://maps.google.com/mapfiles/ms/icons/red-dot.png'),
            });
            marker.addListener('click', () => {
                const action = blocked
                    ? '<div style="margin-top:6px;color:#b91c1c;font-size:12px">Not interested</div>'
                    : onPlan
                        ? '<div style="margin-top:6px;color:#188038;font-size:12px;font-weight:600">Selected</div>'
                        : '<button id="gp-existing-add" type="button" style="margin-top:8px;background:#1a73e8;color:#fff;border:0;border-radius:4px;padding:8px 12px;font-weight:600;cursor:pointer">Select</button>';
                infoRef.current.setContent(`
                    <div style="font-family:Roboto,Arial,sans-serif;max-width:240px">
                        <div style="font-weight:700">${escapeHtml(school.name)}</div>
                        <div style="color:#5f6368;font-size:12px;margin-top:2px">${escapeHtml(school.address || '')}</div>
                        ${action}
                    </div>`);
                infoRef.current.open({ map, anchor: marker });
                if (!blocked && !onPlan) {
                    maps.event.addListenerOnce(infoRef.current, 'domready', () => {
                        document.getElementById('gp-existing-add')?.addEventListener('click', () => {
                            onAddToPlanRef.current?.(school);
                        });
                    });
                }
            });
            schoolMarkersRef.current.push(marker);
        });
        return undefined;
    }, [schools, plannedStopKeys, mapReady]);

    useEffect(() => {
        const map = mapRef.current;
        const maps = window.google?.maps;
        if (!map || !maps || !mapReady) return undefined;
        if (startMarkerRef.current) {
            startMarkerRef.current.setMap(null);
            startMarkerRef.current = null;
        }
        if (startLoc?.lat == null || startLoc?.lng == null) return undefined;
        startMarkerRef.current = new maps.Marker({
            map,
            position: { lat: Number(startLoc.lat), lng: Number(startLoc.lng) },
            draggable: true,
            zIndex: 1000,
            label: { text: 'S', color: '#ffffff', fontWeight: '700' },
            title: 'Start pin',
        });
        startMarkerRef.current.addListener('dragend', (event) => {
            onStartChangeRef.current?.({ lat: event.latLng.lat(), lng: event.latLng.lng() });
        });
        return undefined;
    }, [startLoc, mapReady]);

    useEffect(() => {
        const map = mapRef.current;
        const maps = window.google?.maps;
        if (!map || !maps || !mapReady) return undefined;
        if (routeLineRef.current) {
            routeLineRef.current.setMap(null);
            routeLineRef.current = null;
        }
        const path = [];
        if (startLoc?.lat != null && startLoc?.lng != null) {
            path.push({ lat: Number(startLoc.lat), lng: Number(startLoc.lng) });
        }
        (route || []).forEach((school) => {
            const lat = Number(school.lat);
            const lng = Number(school.lng);
            if (Number.isFinite(lat) && Number.isFinite(lng)) path.push({ lat, lng });
        });
        if (path.length < 2) return undefined;
        routeLineRef.current = new maps.Polyline({
            map,
            path,
            strokeColor: '#1976d2',
            strokeWeight: 4,
            strokeOpacity: 0.9,
            zIndex: 2,
        });
        return undefined;
    }, [route, startLoc, mapReady]);

    return (
        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
            <style>{`.pac-container{z-index:2000 !important;}`}</style>
            <Box
                component="input"
                ref={searchRef}
                placeholder="Private schools, colleges, or locations in Karnataka"
                sx={{
                    position: 'absolute',
                    zIndex: 2,
                    top: 12,
                    left: 0,
                    right: 0,
                    mx: 'auto',
                    width: 'min(480px, calc(100% - 24px))',
                    height: 44,
                    border: 0,
                    borderRadius: '8px',
                    boxShadow: '0 2px 6px rgba(0,0,0,.3)',
                    px: 2,
                    fontSize: 16,
                    outline: 'none',
                    fontFamily: 'Roboto, Arial, sans-serif',
                }}
            />
            <Box ref={mapNodeRef} sx={{ width: '100%', height: '100%' }} />
            <Tooltip title="Use current location">
                <IconButton
                    onClick={() => {
                        if (!navigator.geolocation) {
                            setMapError('Location is not available in this browser.');
                            return;
                        }
                        setLocating(true);
                        setMapError('');
                        navigator.geolocation.getCurrentPosition(
                            (pos) => {
                                const point = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                                const map = mapRef.current;
                                const maps = window.google?.maps;
                                if (map) {
                                    map.panTo(point);
                                    if ((map.getZoom() || 0) < 15) map.setZoom(15);
                                }
                                if (maps && map) {
                                    if (hereMarkerRef.current) hereMarkerRef.current.setMap(null);
                                    hereMarkerRef.current = new maps.Marker({
                                        map,
                                        position: point,
                                        zIndex: 1100,
                                        title: 'Current location',
                                        icon: {
                                            path: maps.SymbolPath.CIRCLE,
                                            scale: 8,
                                            fillColor: '#1a73e8',
                                            fillOpacity: 1,
                                            strokeColor: '#ffffff',
                                            strokeWeight: 2,
                                        },
                                    });
                                }
                                onStartChangeRef.current?.(point);
                                onLocateRef.current?.(point);
                                setLocating(false);
                            },
                            () => {
                                setMapError('Could not get your current location. Allow location access and try again.');
                                setLocating(false);
                            },
                            { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
                        );
                    }}
                    disabled={locating}
                    sx={{
                        position: 'absolute',
                        zIndex: 2,
                        right: 12,
                        bottom: 48,
                        bgcolor: '#fff',
                        boxShadow: 2,
                        '&:hover': { bgcolor: '#f8fafc' },
                    }}
                >
                    {locating ? <CircularProgress size={18} /> : <MyLocationIcon color="primary" />}
                </IconButton>
            </Tooltip>
            <Typography
                variant="caption"
                sx={{
                    position: 'absolute',
                    right: 12,
                    bottom: 12,
                    zIndex: 2,
                    bgcolor: 'rgba(255,255,255,0.92)',
                    px: 1,
                    py: 0.4,
                    borderRadius: 1,
                    boxShadow: 1,
                }}
            >
                <Box component="span" sx={{ color: '#1b5e20', fontWeight: 800 }}>S</Box> start
                {' · path follows selected schools · blue dot is your live location'}
            </Typography>
            {(mapError || busy) && (
                <Typography
                    variant="caption"
                    sx={{
                        position: 'absolute',
                        left: 12,
                        bottom: 12,
                        zIndex: 2,
                        bgcolor: '#fff',
                        px: 1.2,
                        py: 0.6,
                        borderRadius: 1,
                        boxShadow: 2,
                        maxWidth: 360,
                    }}
                >
                    {mapError || 'Searching this area...'}
                </Typography>
            )}
        </Box>
    );
};

// ── Main Component ─────────────────────────────────────────────────────────
const VisitPlanner = (props) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isNarrowScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [forceMobilePreview, setForceMobilePreview] = useState(false);
    const isMobileLayout = forceMobilePreview || isNarrowScreen;
    const [mobileTab, setMobileTab] = useState(0); // 0 = plan, 1 = map
    const [plannerView, setPlannerView] = useState('plan'); // plan | map
    const [mapSelection, setMapSelection] = useState([]);
    const [mapPlanOpen, setMapPlanOpen] = useState(false);

    const isAdmin = useSignupAdmin();

    // ── Form state ──
    const localToday = () => new Date().toLocaleDateString('en-CA');
    const localWeekLater = () => {
        const d = new Date();
        d.setDate(d.getDate() + 7);
        return d.toLocaleDateString('en-CA');
    };
    const [fromDate, setFromDate] = useState(localToday);
    const [toDate, setToDate] = useState(localWeekLater);
    const [mapFromDate, setMapFromDate] = useState(localToday);
    const [mapToDate, setMapToDate] = useState(localToday);
    const [salesPerson, setSalesPerson] = useState('');
    const [salesPersonList, setSalesPersonList] = useState([]);
    const [visitType, setVisitType] = useState('sales');

    // Sales day size. Default 6; values below 6 are rejected with an error.
    const [avgPerDay, setAvgPerDay] = useState(6);
    const [avgError, setAvgError] = useState('');


    const [radiusKm, setRadiusKm] = useState(1);

    // ── Support: our-client locations + manual selection (max 6) ──
    const [clientLocations, setClientLocations] = useState([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [clientFilter, setClientFilter] = useState('');
    const [selectedStops, setSelectedStops] = useState([]);
    const [showAddSchools, setShowAddSchools] = useState(false);
    const [showAddToDay, setShowAddToDay] = useState(false);
    const [dayPlanSearch, setDayPlanSearch] = useState('');
    const [addToDaySearch, setAddToDaySearch] = useState('');
    const [blockingKey, setBlockingKey] = useState('');
    const [blockTarget, setBlockTarget] = useState(null);
    const [blockReason, setBlockReason] = useState('');
    const [addInstituteOpen, setAddInstituteOpen] = useState(false);
    const [newInstName, setNewInstName] = useState('');
    const [newInstType, setNewInstType] = useState('school');
    const [newInstAddress, setNewInstAddress] = useState('');
    const [newInstLoc, setNewInstLoc] = useState(null);
    const [savingInstitute, setSavingInstitute] = useState(false);
    const [mapShareLink, setMapShareLink] = useState('');
    const [resolvingMapLink, setResolvingMapLink] = useState(false);
    const [mapLinkError, setMapLinkError] = useState('');
    const [savingMapSchool, setSavingMapSchool] = useState(false);

    // ── Area autocomplete ──
    const [areaInput, setAreaInput] = useState('');
    const [areaCoords, setAreaCoords] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [areaSuggestions, setAreaSuggestions] = useState([]);
    const [fetchingSuggestions, setFetchingSuggestions] = useState(false);
    const [areaSuggestionObjects, setAreaSuggestionObjects] = useState([]);

    // ── Schools found ──
    const [areaCentre, setAreaCentre] = useState(null);
    const [foundSchools, setFoundSchools] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchProgress, setSearchProgress] = useState(null);

    // ── Plan state ──
    const [plannedDays, setPlannedDays] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [planning, setPlanning] = useState(false);
    const [saving, setSaving] = useState(false);

    // ── Map ──
    const [startLoc, setStartLoc] = useState(DEFAULT_CENTER);
    const [locationPin, setLocationPin] = useState(null);
    const [locatingGPS, setLocatingGPS] = useState(true);
    const [isGPSLocation, setIsGPSLocation] = useState(false);
    const [startLocInput, setStartLocInput] = useState('');
    const [searchingStartLoc, setSearchingStartLoc] = useState(false);
    const [pinMode, setPinMode] = useState(''); // '' | 'start' | 'location'
    const [focusTarget, setFocusTarget] = useState(null);

    // ── Messages ──
    const [statusMsg, setStatusMsg] = useState('');
    const [error, setError] = useState('');

    // Drag-drop refs
    const dragItem = useRef();
    const dragOverItem = useRef();
    const searchRun = useRef(0);
    const searchSchoolsRef = useRef(null);

    // ── GPS on mount: start pin and location pin both open on the current location ──
    useEffect(() => {
        if (!navigator.geolocation) { setLocatingGPS(false); return; }
        let cancelled = false;
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                if (cancelled) return;
                const here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                setStartLoc(here);
                setIsGPSLocation(true);
                setLocationPin(here);
                setAreaCentre(here);
                setAreaCoords(here);
                const fallback = `${here.lat.toFixed(5)}, ${here.lng.toFixed(5)}`;
                try {
                    const place = await reverseGeocode(here.lat, here.lng);
                    if (cancelled) return;
                    const label = place.locality || place.address || fallback;
                    setAreaInput(label);
                    setInputValue(label);
                    setStatusMsg(`Start and location set to ${label}.`);
                } catch {
                    if (cancelled) return;
                    setAreaInput(fallback);
                    setInputValue(fallback);
                    setStatusMsg('Start and location set to your current location.');
                }
                setLocatingGPS(false);
            },
            () => {
                if (cancelled) return;
                setLocatingGPS(false);
                setError('Could not get your current location. Please allow location access.');
            },
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
        );
        return () => { cancelled = true; };
    }, []);

    const handleAreaSelect = (event, newValue) => {
        if (!newValue) {
            setAreaInput('');
            setInputValue('');
            setAreaCoords(null);
            return;
        }
        if (typeof newValue === 'string') {
            setAreaInput(newValue);
            setInputValue(newValue);
            setAreaCoords(null);
            return;
        }
        const label = newValue.main_text
            ? (newValue.secondary_text ? `${newValue.main_text}, ${newValue.secondary_text}` : newValue.main_text)
            : (newValue.description || '');
        setAreaInput(label);
        setInputValue(label);
        const lat = Number(newValue.lat);
        const lng = Number(newValue.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            setAreaCoords(null);
            return;
        }
        const point = { lat, lng };
        setAreaCoords(point);
        setLocationPin(point);
        setAreaCentre(point);
        setFocusTarget({ lat, lng, token: Date.now() });
        setError('');
        setStatusMsg(`Location moved to ${label}.`);
        if (plannerView === 'map') {
            searchSchoolsRef.current?.({ area: label, coords: point });
        }
    };


    // ── Use Current Location Button ──
    const handleUseCurrentLocation = () => {
        if (!navigator.geolocation) {
            setError('Geolocation is not supported by your browser.');
            return;
        }
        setStatusMsg('📍 Locating you...');
        setLocatingGPS(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setStartLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                setIsGPSLocation(true);
                setLocatingGPS(false);
                setStatusMsg('📍 Current location found!');
            },
            () => {
                setLocatingGPS(false);
                setError('Could not get your current location. Please ensure location permissions are enabled.');
            }
        );
    };

    const handlePlanMapLocate = useCallback(async (point) => {
        if (!point?.lat || !point?.lng) return;
        setStartLoc(point);
        setIsGPSLocation(true);
        setLocationPin(point);
        setAreaCentre(point);
        setAreaCoords(point);
        setError('');
        const fallback = `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`;
        let label = fallback;
        try {
            const place = await reverseGeocode(point.lat, point.lng);
            label = place.locality || place.address || fallback;
        } catch { /* coordinates still move the map */ }
        setAreaInput(label);
        setInputValue(label);
        setStatusMsg(`Current location set to ${label}.`);
        searchSchoolsRef.current?.({ area: label, coords: point });
    }, []);

    const handleLocationPinChange = useCallback(async (loc) => {
        if (loc?.lat == null || loc?.lng == null) return;
        const point = { lat: loc.lat, lng: loc.lng };
        const fromMapClick = pinMode === 'location';
        setLocationPin(point);
        setAreaCentre(point);
        setAreaCoords(point);
        setError('');
        if (fromMapClick) setPinMode('');
        if (!fromMapClick && areaInput.trim()) {
            setStatusMsg('📍 Location pin moved. Search again to refresh schools around it.');
            return;
        }
        const fallback = `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`;
        try {
            const place = await reverseGeocode(point.lat, point.lng);
            const label = place.locality || place.address || fallback;
            setAreaInput(label);
            setInputValue(label);
            setStatusMsg(`📍 Location pin set at ${label}. Click Find schools to search around it.`);
        } catch {
            setAreaInput(fallback);
            setInputValue(fallback);
            setStatusMsg('📍 Location pin set. Click Find schools to search around it.');
        }
    }, [pinMode, areaInput]);

    // ── Load sales users from Users API. Full list is admin-only. ──
    useEffect(() => {
        let me = {};
        try { me = JSON.parse(localStorage.getItem('user') || '{}'); } catch { me = {}; }
        if (!isAdmin) {
            if (me?.id) {
                setSalesPersonList([me]);
                setSalesPerson(me.id);
            }
            return;
        }
        getRequest(GET_URL.user.api, { limit: 500, pageno: 1, status: 'active' }, props).then(resp => {
            const rawData = resp?.data;
            let list = [];
            if (rawData && Array.isArray(rawData.data)) list = rawData.data;
            else if (rawData && Array.isArray(rawData.results)) list = rawData.results;
            else if (Array.isArray(rawData)) list = rawData;
            setSalesPersonList(list);
        }).catch(() => { });
    }, [isAdmin]);


    // ── Load Active Academic Year ──
    const [academicYearId, setAcademicYearId] = useState(null);
    useEffect(() => {
        getRequest('/basic/academicyear/', {}, props).then(res => {
            const rawData = res.data;
            let years = [];
            if (rawData && Array.isArray(rawData.data)) years = rawData.data;
            else if (rawData && Array.isArray(rawData.results)) years = rawData.results;
            else if (Array.isArray(rawData)) years = rawData;

            const activeYear = years.find(y => y.is_active);
            if (activeYear) setAcademicYearId(activeYear.id);
            else if (years.length > 0) setAcademicYearId(years[0].id);
        }).catch(() => { });
    }, []);

    // ── Load our-client institutions when Support is involved ──
    useEffect(() => {
        if (!includesSupport(visitType)) {
            setClientLocations([]);
            return;
        }
        let cancelled = false;
        const loadClients = async () => {
            setLoadingClients(true);
            try {
                const supportOnly = visitType === 'support';
                const resp = supportOnly
                    ? await axiosAPI.get('/visits/support-institutes/')
                    : await axiosAPI.get(STORED_PATH, { params: { client: 'yes' } });
                const list = (resp.data?.data || []).filter((inst) => !inst.is_blocked).map((inst) => ({
                    id: inst.id,
                    name: inst.name,
                    address: inst.address || '',
                    lat: inst.lat,
                    lng: inst.lng,
                    phone: inst.phone || '',
                    website: inst.website || '',
                    type: (inst.type || inst.institution_type || 'school').toLowerCase() === 'college'
                        ? 'college'
                        : 'school',
                    client_status: 'Y',
                    is_client: true,
                    company_id: supportOnly ? inst.id : null,
                    visit_count: inst.visit_count || 0,
                    last_interest_status: inst.last_interest_status || inst.interest_status || '',
                    last_visit_date: inst.last_visit_date || '',
                    interest_status: inst.last_interest_status || inst.interest_status || '',
                    not_interested: (inst.last_interest_status || inst.interest_status) === 'not_interested',
                }));
                if (!cancelled) setClientLocations(list);
            } catch {
                if (!cancelled) {
                    setClientLocations([]);
                    setError(visitType === 'support'
                        ? 'Failed to load institutes from the company list.'
                        : 'Failed to load our client locations.');
                }
            } finally {
                if (!cancelled) setLoadingClients(false);
            }
        };
        loadClients();
        return () => { cancelled = true; };
    }, [visitType]);

    useEffect(() => {
        // Reset selection when switching visit type
        setSelectedStops([]);
        setPlannedDays([]);
        setSelectedDate('');
        setFoundSchools([]);
        setAreaCentre(null);
        setShowAddToDay(false);
        setDayPlanSearch('');
        setAddToDaySearch('');
        if (visitType === 'support' && fromDate) {
            setToDate(fromDate);
        }
        // Sales & Support: always show area search to pick other schools alongside our clients
        if (isSalesAndSupport(visitType)) setShowAddSchools(true);
        else if (!includesSupport(visitType)) setShowAddSchools(false);
        else setShowAddSchools(false);
    }, [visitType, fromDate]);

    const isStopSelected = useCallback(
        (school) => selectedStops.some((s) => stopKey(s) === stopKey(school)),
        [selectedStops]
    );

    const toggleStopSelection = useCallback((school) => {
        setSelectedStops((prev) => {
            const key = stopKey(school);
            const exists = prev.some((s) => stopKey(s) === key);
            if (exists) return prev.filter((s) => stopKey(s) !== key);
            // Support day-wise: no hard cap. Sales & Support still max 6.
            const cap = visitType === 'support' ? null : MAX_SELECT;
            if (cap != null && prev.length >= cap) {
                setError(`You can select a maximum of ${cap} locations.`);
                return prev;
            }
            setError('');
            return [...prev, school];
        });
    }, [visitType]);

    const filteredClientLocations = clientLocations.filter((c) => {
        if (!clientFilter.trim()) return true;
        const q = clientFilter.trim().toLowerCase();
        return (c.name || '').toLowerCase().includes(q) || (c.address || '').toLowerCase().includes(q);
    });

    const originLat = locationPin?.lat ?? areaCoords?.lat ?? areaCentre?.lat ?? startLoc?.lat;
    const originLng = locationPin?.lng ?? areaCoords?.lng ?? areaCentre?.lng ?? startLoc?.lng;

    useEffect(() => {
        if (!inputValue || inputValue.length < 2) { setAreaSuggestions([]); setAreaSuggestionObjects([]); return; }
        const t = setTimeout(async () => {
            setFetchingSuggestions(true);
            try {
                const resp = await axiosAPI.get(SUGGEST_PATH, {
                    params: {
                        input: inputValue,
                        ...(originLat != null && originLng != null ? { lat: originLat, lng: originLng } : {}),
                    },
                });
                const suggs = resp.data?.suggestions || [];
                setAreaSuggestionObjects(suggs);
                setAreaSuggestions(suggs);
            } catch {
                setAreaSuggestions([]);
                setAreaSuggestionObjects([]);
            } finally { setFetchingSuggestions(false); }
        }, 400);
        return () => clearTimeout(t);
    }, [inputValue, originLat, originLng]);

    useEffect(() => {
        if (visitType === 'support') {
            setAvgError('');
            return;
        }
        const n = parseInt(avgPerDay, 10);
        const min = visitType === 'sales' ? 6 : 1;
        if (avgPerDay === '' || Number.isNaN(n) || n < min) {
            setAvgError(visitType === 'sales'
                ? 'Enter 6 or more schools per day.'
                : 'Enter at least 1 per day.');
        } else {
            setAvgError('');
        }
    }, [avgPerDay, visitType]);

    // ── Search schools ──
    // 10 km and 15 km run as separate 5 km requests (centre, then rings) so
    // production does not time out on one large lookup. Schools appear as each
    // step returns. The area name is sent only for the centre tile; ring tiles
    // are cached by their own coordinates.
    const handleSearchSchools = async (overrides = {}) => {
        const coords = overrides.coords || locationPin || areaCoords;
        const area = (overrides.area != null ? overrides.area : areaInput).trim()
            || (coords ? `${Number(coords.lat).toFixed(5)}, ${Number(coords.lng).toFixed(5)}` : '');
        if (!area) { setError('Please select an area or drop the location pin on the map.'); return; }
        const runId = searchRun.current + 1;
        searchRun.current = runId;
        setError('');
        setSearching(true);
        setFoundSchools([]);
        setPlannedDays([]);
        setSelectedDate('');
        const searchCoords = coords;
        const usesRings = radiusKm > TILE_RADIUS_KM;
        let origin = searchCoords?.lat != null && searchCoords?.lng != null
            ? { lat: Number(searchCoords.lat), lng: Number(searchCoords.lng) }
            : null;
        if (origin) {
            setAreaCentre(origin);
            setLocationPin(origin);
        }
        const tiles = origin
            ? radiusSearchTiles(origin, radiusKm)
            : [{ center: null, radiusKm: usesRings ? TILE_RADIUS_KM : radiusKm, ring: 'centre' }];
        setSearchProgress({ done: 0, total: tiles.length });
        setStatusMsg(
            usesRings
                ? `Searching inner 5 km (1 of ${tiles.length}) for private & aided schools within ${radiusKm} km...`
                : `Searching for private & aided schools and colleges within ${radiusKm} km...`
        );

        const seen = new Set();
        let merged = [];
        let failed = 0;
        let fromDb = true;
        let centreFailed = false;

        const stillCurrent = () => searchRun.current === runId;

        try {
            for (let i = 0; i < tiles.length; i += 1) {
                if (!stillCurrent()) return;
                const tile = tiles[i];
                setStatusMsg(
                    usesRings
                        ? `Searching ${ringLabel(tile.ring)} (${i + 1} of ${tiles.length}) for private & aided schools within ${radiusKm} km...`
                        : `Searching for private & aided schools and colleges within ${radiusKm} km...`
                );
                try {
                    const resp = await axiosAPI.post(NEARBY_PATH, {
                        ...(tile.ring === 'centre' ? { area } : {}),
                        radius_km: tile.radiusKm,
                        ...(tile.center ? { lat: tile.center.lat, lng: tile.center.lng } : {}),
                    });
                    if (!stillCurrent()) return;
                    const data = resp.data || {};
                    if (!(data.cached || data.source === 'database')) fromDb = false;
                    const centre = data.center || tile.center;
                    if (!origin && centre?.lat != null && centre?.lng != null) {
                        origin = { lat: Number(centre.lat), lng: Number(centre.lng) };
                        setAreaCentre(origin);
                        setLocationPin(origin);
                        if (usesRings) {
                            tiles.push(...radiusSearchTiles(origin, radiusKm).slice(1));
                        }
                    } else if (i === 0 && centre?.lat != null && centre?.lng != null) {
                        const resolved = { lat: Number(centre.lat), lng: Number(centre.lng) };
                        setAreaCentre(resolved);
                        setLocationPin(resolved);
                    }
                    (data.schools || []).forEach((school) => {
                        if (!isPrivateOrAided(school)) return;
                        const point = { lat: Number(school.lat), lng: Number(school.lng) };
                        const fromOrigin = origin ? haversineKm(origin, point) : Number(school.distance_km);
                        if (origin && fromOrigin > radiusKm + 0.05) return;
                        const next = {
                            ...school,
                            address: school.address || school.vicinity || [school.name, area].filter(Boolean).join(', '),
                            distance_km: origin && Number.isFinite(fromOrigin)
                                ? Math.round(fromOrigin * 100) / 100
                                : school.distance_km,
                        };
                        const key = schoolDedupeKey(next);
                        if (seen.has(key)) return;
                        seen.add(key);
                        merged.push(next);
                    });
                    merged.sort((a, b) => (a.distance_km ?? 9999) - (b.distance_km ?? 9999));
                    setFoundSchools([...merged]);
                } catch (err) {
                    failed += 1;
                    if (i === 0 && merged.length === 0) {
                        centreFailed = true;
                        if (stillCurrent()) {
                            setError(err.response?.data?.error || 'Failed to search schools.');
                            setStatusMsg('');
                        }
                        return;
                    }
                }
                if (stillCurrent()) setSearchProgress({ done: i + 1, total: tiles.length });
            }
            if (!stillCurrent() || centreFailed) return;
            if (merged.length === 0) {
                setError(`No private or aided schools/colleges found within ${radiusKm} km. Try a larger radius or different area.`);
                setStatusMsg('');
                return;
            }
            const notInterestedCount = merged.filter(isNotInterestedSchool).length;
            const baseMsg = fromDb
                ? `Loaded ${merged.length} private/aided school(s)/college(s) within ${radiusKm} km from database.`
                : `Found ${merged.length} private/aided school(s)/college(s) within ${radiusKm} km — saved to database.`;
            const failMsg = failed > 0 ? ` ${failed} search step(s) could not finish.` : '';
            setStatusMsg(
                notInterestedCount > 0
                    ? `${baseMsg}${failMsg} ${notInterestedCount} already visited & not interested (🚫) — excluded from planning.`
                    : `${baseMsg}${failMsg}`
            );
        } finally {
            if (stillCurrent()) {
                setSearching(false);
                setSearchProgress(null);
            }
        }
    };
    searchSchoolsRef.current = handleSearchSchools;

    // ── Search Start Location ──
    const handleSearchStartLocation = async () => {
        if (!startLocInput.trim()) return;
        setSearchingStartLoc(true);
        setError('');
        try {
            const place = await geocodePlace(startLocInput);
            setStartLoc({ lat: place.lat, lng: place.lng });
            setStatusMsg('🟢 Start location updated.');
        } catch (err) {
            setError('Error searching for start location.');
        } finally {
            setSearchingStartLoc(false);
        }
    };

    // ── Generate plan ──
    const handleGeneratePlan = () => {
        if (!fromDate || !toDate) { setError('Please select From Date and To Date.'); return; }
        if (new Date(toDate) < new Date(fromDate)) { setError('To Date must be after From Date.'); return; }

        const supportOnly = visitType === 'support';
        const salesSupport = isSalesAndSupport(visitType);
        const salesOnly = visitType === 'sales';
        const minAvg = salesOnly ? 6 : 1;
        const parsedAvg = parseInt(avgPerDay, 10);
        if (!parsedAvg || parsedAvg < minAvg) {
            setError(salesOnly
                ? 'Avg schools per day cannot be less than 6.'
                : 'Enter at least 1 institute per day.');
            return;
        }

        // Support: day-wise from our institutes only (selected subset, or all clients)
        // Sales & Support: manual max-6 mix
        // Sales: nearby found schools
        let sourceSchools;
        if (supportOnly) {
            const pool = selectedStops.length > 0 ? selectedStops : clientLocations;
            sourceSchools = pool.filter((s) => s.is_client || (s.client_status || '').toUpperCase() === 'Y');
            if (sourceSchools.length === 0) {
                setError('No institutes found in the company list. Add companies, or select from the list.');
                return;
            }
        } else if (salesSupport) {
            sourceSchools = selectedStops;
            if (selectedStops.length === 0) {
                setError('Select our schools and/or other schools (max 6), then plan schedule.');
                return;
            }
        } else {
            sourceSchools = foundSchools;
            if (foundSchools.length === 0) {
                setError('Search for schools first.');
                return;
            }
        }

        setError('');
        setStatusMsg('Building optimised day-by-day plan...');
        setPlanning(true);

        const dates = datesBetween(fromDate, toDate);

        const perDay = salesSupport
            ? Math.max(1, Math.min(MAX_SELECT, parsedAvg))
            : parsedAvg;

        const skippedNotInterested = sourceSchools.filter(isNotInterestedSchool).length;
            const filteredSchools = sourceSchools.filter(school => {
            if (isNotInterestedSchool(school)) return false;
            if (supportOnly || school.is_client) return true; // our institutes always kept
            return isPrivateOrAided(school);
        });

        const anchor = (startLoc?.lat != null && startLoc?.lng != null)
            ? startLoc
            : (locationPin || areaCentre);
        const dayRoutes = planDaysByProximity(filteredSchools, anchor, perDay, salesSupport);

        if (dayRoutes.length === 0 || dayRoutes.every((route) => route.length === 0)) {
            setError(
                skippedNotInterested > 0
                    ? 'All institutes were already marked not interested (🚫), so none were scheduled.'
                    : supportOnly
                        ? 'No company institutes left to schedule.'
                        : 'All found schools were government schools, so none were scheduled.'
            );
            setPlanning(false);
            return;
        }

        const days = dates.map((date, i) => ({
            date,
            route: dayRoutes[i] || [],
        })).filter(d => d.route.length > 0);

        // Sales requires full days of 6+; support / sales+support allow partial last days
        const finalDays = salesOnly
            ? days.filter(d => d.route.length >= 6)
            : days;

        if (finalDays.length === 0) {
            setError(
                supportOnly
                    ? 'Could not build a day-wise plan from company institutes for the selected dates.'
                    : salesSupport
                        ? 'Could not build a schedule from the selected locations.'
                        : 'Not enough eligible schools found to create a plan with minimum 6 visits per day (government & 🚫 not-interested excluded).'
            );
            setPlanning(false);
            return;
        }

        setPlannedDays(finalDays);
        setSelectedDate(finalDays[0]?.date || '');
        setShowAddToDay(false);
        const skipNote = skippedNotInterested > 0
            ? ` ${skippedNotInterested} not-interested (🚫) excluded.`
            : '';
        const typeNote = ` Visit type: ${visitTypeLabel(visitType)}.`;
        const oursCount = finalDays.reduce(
            (sum, day) => sum + day.route.filter((s) => s.is_client).length,
            0
        );
        const supportNote = supportOnly
            ? ` ${oursCount} company institute(s).`
            : '';
        const priorityNote = salesSupport && oursCount > 0
            ? ' Our schools are preferred when two nearby groups are similar.'
            : '';
        setStatusMsg(`Plan ready — ${finalDays.length} day(s). Each day is a tight group of nearby schools, including schools in other sectors.${typeNote}${supportNote}${priorityNote}${skipNote}`);
        setPlanning(false);
    };

    // ── Save plan ──
    const handleSavePlan = async (daysOverride) => {
        const daysToSave = Array.isArray(daysOverride) ? daysOverride : plannedDays;
        if (!salesPerson) { setError('Please select a Sales Person before saving.'); return; }
        if (daysToSave.length === 0) { setError('Generate a plan first.'); return; }
        setSaving(true); setStatusMsg('Saving route to database...');
        try {
            // Map selected User → Person profile used by visits
            const resolveRes = await axiosAPI.post('/visits/resolve-user/', { user_id: salesPerson });
            const personId = resolveRes.data?.person_id;
            if (!personId) throw new Error('Could not resolve selected user to a sales person profile.');

            // Fetch existing visits to prevent duplicates.
            // Support plans are listed under Support visits, not Sales visits.
            let existingVisits = [];
            const duplicateKinds = visitType === 'sales_support'
                ? ['sales', 'support']
                : [visitType === 'support' ? 'support' : 'sales'];
            try {
                for (const kind of duplicateKinds) {
                    const res = await axiosAPI.get('/visits/', {
                        params: { academic_year_id: academicYearId, page_size: 1000, visit_kind: kind }
                    });
                    const rows = res.data?.data || res.data?.results || [];
                    existingVisits = existingVisits.concat(rows);
                }
            } catch (e) {
                console.warn("Could not fetch existing visits to check duplicates", e);
            }

            for (const day of daysToSave) {
                const dateStr = day.date; // e.g. YYYY-MM-DD

                for (const school of day.route) {
                    // Truncate to 300 chars max (backend limit)
                    const rawAreaName = school.name + (school.address ? ', ' + school.address : (school.area ? ', ' + school.area : ''));
                    const fullAreaName = rawAreaName.length > 300 ? rawAreaName.substring(0, 297) + '...' : rawAreaName;

                    // Check duplicate
                    const isDuplicate = existingVisits.some(v =>
                        (v.area_name === fullAreaName || v.area_name?.startsWith(school.name)) &&
                        (v.planned_date === dateStr || v.planned_date?.startsWith(dateStr))
                    );

                    if (isDuplicate) {
                        console.log("Skipping duplicate:", fullAreaName);
                        continue;
                    }

                    // Leave person blank/omitted as discussed in the plan
                    const payload = {
                        person: personId,
                        user_id: salesPerson,
                        area_name: fullAreaName,
                        planned_date: dateStr,
                        latitude: school.lat ? school.lat.toFixed(7) : '',
                        longitude: school.lng ? school.lng.toFixed(7) : '',
                        contact_number: school.phone || '',
                        interest_status: 'pending',
                        wants_parents_orientation: false,
                        wants_student_orientation: false,
                        wants_staff_orientation: false,
                        wants_revisit: false,
                        academic_year: academicYearId || null,
                        notes: `Visit type: ${visitTypeLabel(visitType)}${school.is_client ? ' | Our Client' : ''}`,
                    };
                    // Save one stop at a time so My Visits keeps this route order.
                    await axiosAPI.post('/visits/', payload);
                }
            }

            const allSchools = daysToSave.flatMap(day => day.route).map(school => ({
                name: school.name,
                address: school.address || '',
                latitude: school.lat,
                longitude: school.lng,
                phone: school.phone || '',
                website: school.website || '',
            }));
            if (allSchools.length > 0) {
                try {
                    await axiosAPI.post(BULK_SAVE_PATH, {
                        institutions: allSchools
                    });
                    console.log('Schools/Colleges bulk-saved to schools_colleges table.');
                } catch (e) {
                    console.warn('Could not bulk-save institutions:', e);
                }
            }
            setStatusMsg('✅ Route saved successfully!');
            setTimeout(() => navigate('/sales/my-visits'), 1000);
        } catch (err) {
            console.error("Save error:", err);
            const detail = err.response?.data
                ? JSON.stringify(err.response.data)
                : err.message;
            setError(`Save failed: ${detail}`);
            setStatusMsg('');
        } finally { setSaving(false); }
    };

    const handleSaveSupportPlan = async () => {
        if (!salesPerson) { setError('Please select a Sales Person before saving.'); return; }
        if (!fromDate) { setError('Please select a Date.'); return; }
        if (selectedStops.length === 0) {
            setError('Please select at least one institute to visit.');
            return;
        }
        await handleSavePlan([{
            date: fromDate,
            route: selectedStops,
        }]);
    };

    // ── Drag-drop reorder ──
    const activeDayData = plannedDays.find(d => d.date === selectedDate);
    const activeDayIndex = plannedDays.findIndex(d => d.date === selectedDate);
    const currentRoute = activeDayData?.route || [];

    const handleDrop = () => {
        const copy = [...plannedDays];
        const route = [...copy[activeDayIndex].route];
        const item = route.splice(dragItem.current, 1)[0];
        route.splice(dragOverItem.current, 0, item);
        copy[activeDayIndex].route = route;
        setPlannedDays(copy);
        dragItem.current = dragOverItem.current = null;
    };

    const deleteStop = (dayIdx, stopIdx) => {
        const copy = [...plannedDays];
        copy[dayIdx].route.splice(stopIdx, 1);
        setPlannedDays([...copy]);
    };

    const plannedStopKeys = React.useMemo(() => {
        const keys = new Set();
        plannedDays.forEach((d) => (d.route || []).forEach((s) => keys.add(stopKey(s))));
        return keys;
    }, [plannedDays]);

    const addableSchoolsForDay = React.useMemo(() => {
        const pool = [];
        const seen = new Set();
        const pushUnique = (school) => {
            if (!school || isNotInterestedSchool(school)) return;
            const key = stopKey(school);
            if (seen.has(key) || plannedStopKeys.has(key)) return;
            seen.add(key);
            pool.push(school);
        };
        // Support: our institutes only
        if (visitType === 'support') {
            clientLocations.forEach((c) => pushUnique(c));
            return pool;
        }
        if (includesSupport(visitType)) {
            clientLocations.forEach((c) => pushUnique(c));
        }
        foundSchools.forEach((s) => {
            const matchedClient = clientLocations.some((c) => stopKey(c) === stopKey(s));
            pushUnique({ ...s, is_client: Boolean(s.is_client) || matchedClient });
        });
        return includesSupport(visitType) ? prioritizeOurSchools(pool) : pool;
    }, [clientLocations, foundSchools, plannedStopKeys, visitType]);

    const addStopToDay = useCallback((school) => {
        if (activeDayIndex < 0) {
            setError('Select a day tab first.');
            return;
        }
        const key = stopKey(school);
        const alreadyOnDay = (plannedDays[activeDayIndex]?.route || []).some((s) => stopKey(s) === key);
        if (alreadyOnDay || plannedStopKeys.has(key)) {
            setError('That school is already on the plan.');
            return;
        }
        const copy = plannedDays.map((d, i) =>
            i === activeDayIndex
                ? { ...d, route: [...d.route, { ...school }] }
                : d
        );
        setPlannedDays(copy);
        setError('');
        setStatusMsg(`Added “${school.name}” to ${fmt(selectedDate)}.`);
    }, [activeDayIndex, plannedDays, plannedStopKeys, selectedDate]);

    const addSchoolFromMap = useCallback((school) => {
        if (isNotInterestedSchool(school)) {
            setError('This school is marked not interested and stays off the plan.');
            return;
        }
        setMapSelection((prev) => {
            if (prev.some((item) => stopKey(item) === stopKey(school))) return prev;
            return [...prev, { ...school }];
        });
        setError('');
        setStatusMsg(`Selected “${school.name}”. Choose Create plan when the list is ready.`);
    }, []);

    const submitMapPlan = async () => {
        if (!mapFromDate) {
            setError('Choose a date.');
            return;
        }
        if (visitType !== 'support' && !mapToDate) {
            setError('Choose a from date and a to date.');
            return;
        }
        if (visitType !== 'support' && new Date(mapToDate) < new Date(mapFromDate)) {
            setError('To date must be on or after the from date.');
            return;
        }
        const schools = mapSelection.filter((school) => !isNotInterestedSchool(school));
        if (schools.length === 0) {
            setError('Select schools on the map first.');
            return;
        }
        if (visitType === 'support') {
            const days = [{ date: mapFromDate, route: schools }];
            setFromDate(mapFromDate);
            setToDate(mapFromDate);
            setPlannedDays(days);
            setSelectedDate(mapFromDate);
            setMapPlanOpen(false);
            await handleSavePlan(days);
            return;
        }
        const dates = datesBetween(mapFromDate, mapToDate);
        if (dates.length === 0) {
            setError('That range has no working days.');
            return;
        }
        const perDay = Math.ceil(schools.length / dates.length);
        const days = [];
        let index = 0;
        dates.forEach((date) => {
            const route = schools.slice(index, index + perDay);
            index += route.length;
            if (route.length) days.push({ date, route });
        });
        setFromDate(mapFromDate);
        setToDate(mapToDate);
        setPlannedDays(days);
        setSelectedDate(days[0]?.date || '');
        setMapPlanOpen(false);
        await handleSavePlan(days);
    };

    const handleGooglePlace = useCallback((label, point) => {
        setAreaInput(label);
        setInputValue(label);
        setAreaCoords(point);
        setLocationPin(point);
        setAreaCentre(point);
        setError('');
        searchSchoolsRef.current?.({ area: label, coords: point });
    }, []);

    const saveMapPickedSchool = async (pick) => {
        const name = (pick?.name || '').trim();
        if (!name) {
            setError('Enter the school name.');
            return false;
        }
        if (pick?.lat == null || pick?.lng == null) {
            setError('Click the map to choose a location.');
            return false;
        }
        setSavingMapSchool(true);
        setError('');
        try {
            const resp = await axiosAPI.post(STORED_PATH, {
                name,
                type: pick.type || 'school',
                address: pick.address || '',
                lat: pick.lat,
                lng: pick.lng,
                source: 'MANUAL',
            });
            const saved = resp.data?.data || {};
            const school = {
                id: saved.id,
                name: saved.name || name,
                address: saved.address || pick.address || '',
                lat: Number(saved.lat ?? pick.lat),
                lng: Number(saved.lng ?? pick.lng),
                type: (saved.type || pick.type || 'school').toLowerCase() === 'college' ? 'college' : 'school',
                phone: saved.phone || '',
                manual: true,
                is_client: saved.client_status === 'Y',
            };
            setFoundSchools((prev) => {
                if (prev.some((item) => stopKey(item) === stopKey(school))) return prev;
                return [...prev, school];
            });
            setFocusTarget({ lat: school.lat, lng: school.lng, token: Date.now() });
            addSchoolFromMap(school);
            return true;
        } catch (err) {
            setError(err.response?.data?.error || 'Could not add this school.');
            return false;
        } finally {
            setSavingMapSchool(false);
        }
    };

    const schoolMatchesQuery = (school, query) => {
        const q = (query || '').trim().toLowerCase();
        if (!q) return true;
        return (school?.name || '').toLowerCase().includes(q)
            || (school?.address || '').toLowerCase().includes(q)
            || (school?.area || '').toLowerCase().includes(q);
    };

    const visibleDayStops = currentRoute
        .map((school, idx) => ({ school, idx }))
        .filter(({ school }) => schoolMatchesQuery(school, dayPlanSearch));

    const visibleAddableSchools = addableSchoolsForDay.filter((school) => schoolMatchesQuery(school, addToDaySearch));

    const mapSchools = React.useMemo(() => {
        const base = visitType === 'support'
            ? (selectedStops.length > 0 ? selectedStops : clientLocations)
            : (includesSupport(visitType) && selectedStops.length > 0 ? selectedStops : foundSchools);
        const keys = new Set(base.map((school) => stopKey(school)));
        const extras = foundSchools.filter((school) => school.manual && !keys.has(stopKey(school)));
        return [...base, ...extras];
    }, [visitType, selectedStops, clientLocations, foundSchools]);

    const mapViewSchools = React.useMemo(() => {
        const seen = new Set();
        const list = [];
        [...mapSchools, ...mapSelection].forEach((school) => {
            const key = stopKey(school);
            if (seen.has(key)) return;
            seen.add(key);
            list.push(school);
        });
        return list;
    }, [mapSchools, mapSelection]);

    const mapSelectionKeys = React.useMemo(
        () => new Set(mapSelection.map((school) => stopKey(school))),
        [mapSelection]
    );

    const forgetSchool = useCallback((school) => {
        const key = stopKey(school);
        const name = (school?.name || '').trim().toLowerCase();
        const drop = (item) => {
            if (stopKey(item) === key) return false;
            const sameName = name && (item?.name || '').trim().toLowerCase() === name;
            const near = school?.lat != null && item?.lat != null
                && Math.abs(Number(item.lat) - Number(school.lat)) <= 0.002
                && Math.abs(Number(item.lng) - Number(school.lng)) <= 0.002;
            return !(sameName && near);
        };
        setFoundSchools((prev) => prev.filter(drop));
        setClientLocations((prev) => prev.filter(drop));
        setSelectedStops((prev) => prev.filter(drop));
        setMapSelection((prev) => prev.filter(drop));
        setPlannedDays((prev) => prev.map((day) => ({
            ...day,
            route: (day.route || []).filter(drop),
        })));
    }, []);

    const openBlockDialog = (school) => {
        setBlockTarget(school);
        setBlockReason('');
        setError('');
    };

    const blockSchool = async () => {
        const school = blockTarget;
        const reason = blockReason.trim();
        if (!school) return;
        if (!reason) {
            setError('Enter a reason to block this institute.');
            return;
        }
        const key = stopKey(school);
        setBlockingKey(key);
        setError('');
        try {
            const numericId = school?.id != null && /^\d+$/.test(String(school.id)) ? school.id : undefined;
            await axiosAPI.patch(STORED_PATH, {
                id: numericId,
                name: school.name,
                address: school.address || '',
                lat: school.lat,
                lng: school.lng,
                type: school.type || 'school',
                phone: school.phone || '',
                is_blocked: true,
                block_reason: reason,
            });
            forgetSchool(school);
            setBlockTarget(null);
            setBlockReason('');
            setStatusMsg(`Blocked “${school.name}”. It stays hidden until you unblock it in All Schools → Blocked.`);
        } catch (err) {
            setError(err.response?.data?.error || 'Could not block this school.');
        } finally {
            setBlockingKey('');
        }
    };

    const pickInstituteLocation = useCallback(async (point) => {
        if (point?.lat == null || point?.lng == null) return;
        setNewInstLoc({ lat: point.lat, lng: point.lng, address: '' });
        try {
            const place = await reverseGeocode(point.lat, point.lng);
            const label = place.address || '';
            setNewInstLoc({ lat: point.lat, lng: point.lng, address: label });
            if (label) {
                setNewInstAddress((prev) => (prev.trim() ? prev : label));
            }
        } catch {
            /* pin stays at the clicked point */
        }
    }, []);

    const resetNewInstitute = () => {
        setNewInstName('');
        setNewInstType('school');
        setNewInstAddress('');
        setNewInstLoc(null);
        setMapShareLink('');
        setMapLinkError('');
    };

    const searchByMapShareLink = async () => {
        const raw = mapShareLink.trim();
        if (!raw) {
            setMapLinkError('Paste a Google Maps share link.');
            return;
        }
        if (!isGoogleMapLink(raw)) {
            setMapLinkError('Paste a Google Maps link, such as maps.app.goo.gl or google.com/maps.');
            return;
        }
        setResolvingMapLink(true);
        setMapLinkError('');
        setError('');
        try {
            let point = extractMapCoordinates(raw);
            let placeName = placeNameFromMapUrl(raw);
            if (!point) {
                const resp = await axiosAPI.post(RESOLVE_MAP_LINK_PATH, { url: raw });
                const lat = Number(resp.data?.lat);
                const lng = Number(resp.data?.lng);
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                    setMapLinkError('No location found in that link.');
                    return;
                }
                point = { lat, lng };
                placeName = placeName || (resp.data?.name || '').trim();
            }
            await pickInstituteLocation(point);
            if (placeName && !newInstName.trim()) setNewInstName(placeName);
        } catch (err) {
            setMapLinkError(err.response?.data?.error || 'Could not read that Google Maps link.');
        } finally {
            setResolvingMapLink(false);
        }
    };

    const saveNewInstitute = async () => {
        const name = newInstName.trim();
        if (!name) {
            setError('Enter the institute name.');
            return;
        }
        if (newInstLoc?.lat == null || newInstLoc?.lng == null) {
            setError('Search and pick a location for this institute.');
            return;
        }
        setSavingInstitute(true);
        setError('');
        try {
            const resp = await axiosAPI.post(STORED_PATH, {
                name,
                type: newInstType,
                address: newInstAddress.trim() || newInstLoc.address || '',
                lat: newInstLoc.lat,
                lng: newInstLoc.lng,
                source: 'MANUAL',
            });
            const saved = resp.data?.data || {};
            const school = {
                id: saved.id,
                name: saved.name || name,
                address: saved.address || newInstAddress.trim() || newInstLoc.address || '',
                lat: Number(saved.lat ?? newInstLoc.lat),
                lng: Number(saved.lng ?? newInstLoc.lng),
                type: (saved.type || newInstType || 'school').toLowerCase() === 'college' ? 'college' : 'school',
                phone: saved.phone || '',
                manual: true,
                is_client: saved.client_status === 'Y',
            };
            setFoundSchools((prev) => {
                if (prev.some((item) => stopKey(item) === stopKey(school))) return prev;
                return [...prev, school];
            });
            if (activeDayIndex >= 0) {
                setPlannedDays((prev) => prev.map((day, index) => {
                    if (index !== activeDayIndex) return day;
                    if ((day.route || []).some((item) => stopKey(item) === stopKey(school))) return day;
                    return { ...day, route: [...day.route, school] };
                }));
            }
            setFocusTarget({ lat: school.lat, lng: school.lng, token: Date.now() });
            if (isMobileLayout) setMobileTab(1);
            setStatusMsg(`Added “${school.name}” and placed it on the map.`);
            setAddInstituteOpen(false);
            resetNewInstitute();
        } catch (err) {
            setError(err.response?.data?.error || 'Could not add this institute.');
        } finally {
            setSavingInstitute(false);
        }
    };

    const today = new Date().toLocaleDateString('en-CA');
    const showPhoneFrame = forceMobilePreview && !isNarrowScreen;

    // ─────────────────────────────────────────────────────────────────────
    return (
        <Box
            sx={{
                position: 'relative',
                height: 'calc(100vh - 70px)',
                overflow: 'hidden',
                p: isMobileLayout ? 1 : 2,
                bgcolor: showPhoneFrame ? '#e8ecf1' : 'transparent',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Top-left: switch to mobile / desktop preview */}
            {!isNarrowScreen && (
                <Tooltip title={forceMobilePreview ? 'Switch to desktop layout' : 'Preview mobile layout'}>
                    <Button
                        size="small"
                        variant={forceMobilePreview ? 'contained' : 'outlined'}
                        color="primary"
                        startIcon={forceMobilePreview ? <DesktopWindowsIcon /> : <PhoneAndroidIcon />}
                        onClick={() => {
                            setForceMobilePreview((v) => !v);
                            setMobileTab(0);
                        }}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            zIndex: 1300,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 2,
                            bgcolor: forceMobilePreview ? undefined : 'rgba(255,255,255,0.95)',
                            boxShadow: 2,
                        }}
                    >
                        {forceMobilePreview ? 'Desktop' : 'Mobile'}
                    </Button>
                </Tooltip>
            )}

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    overflow: 'hidden',
                    mt: !isNarrowScreen ? 4.5 : 0,
                    ...(showPhoneFrame
                        ? {
                            width: 390,
                            maxWidth: '100%',
                            mx: 'auto',
                            border: '10px solid #1f2937',
                            borderRadius: '28px',
                            boxShadow: '0 18px 40px rgba(15,23,42,0.28)',
                            bgcolor: '#f5f7fb',
                            p: 1,
                            mt: 5,
                        }
                        : {}),
                }}
            >
                <Tabs
                    value={plannerView}
                    onChange={(_, value) => {
                        if (!value) return;
                        setPlannerView(value);
                        if (value === 'map') setPinMode('');
                    }}
                    variant="fullWidth"
                    sx={{
                        flexShrink: 0,
                        minHeight: 40,
                        bgcolor: '#fff',
                        borderRadius: 2,
                        border: '1px solid #dde6ff',
                        '& .MuiTab-root': { minHeight: 40, py: 0.5, textTransform: 'none', fontWeight: 700 },
                    }}
                    TabIndicatorProps={{ style: { background: '#4680FF' } }}
                >
                    <Tab value="plan" label="Plan" />
                    <Tab value="map" label="Plan by Map" />
                </Tabs>
                {isMobileLayout && plannerView === 'plan' && (
                    <Tabs
                        value={mobileTab}
                        onChange={(_, v) => setMobileTab(v)}
                        variant="fullWidth"
                        sx={{
                            flexShrink: 0,
                            minHeight: 40,
                            bgcolor: '#fff',
                            borderRadius: 2,
                            border: '1px solid #dde6ff',
                            '& .MuiTab-root': { minHeight: 40, py: 0.5, textTransform: 'none', fontWeight: 700 },
                        }}
                        TabIndicatorProps={{ style: { background: '#4680FF' } }}
                    >
                        <Tab label="Plan" />
                        <Tab label="Map" />
                    </Tabs>
                )}

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: isMobileLayout ? 'column' : 'row',
                    gap: isMobileLayout ? 1 : 2,
                    overflow: 'hidden',
                }}
            >
            {/* ── LEFT PANEL ── */}
            <Box
                sx={{
                    width: isMobileLayout ? '100%' : 400,
                    flexShrink: 0,
                    display: plannerView === 'map' || (isMobileLayout && mobileTab !== 0) ? 'none' : 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    overflowY: 'auto',
                    pr: isMobileLayout ? 0 : 0.5,
                    flex: isMobileLayout ? 1 : undefined,
                    minHeight: 0,
                }}
            >
                {/* ── CONFIG CARD ── */}
                <Paper elevation={3} sx={{ p: 2.5, borderRadius: 2, background: 'linear-gradient(135deg,#f8faff 0%,#f0f4ff 100%)', border: '1px solid #dde6ff' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <AutoAwesomeIcon sx={{ color: '#4680FF' }} />
                        <Typography variant="h6" fontWeight="bold" color="primary">
                            Visit Planner
                        </Typography>
                        <Chip size="small" label={visitTypeLabel(visitType)} color="primary" variant="outlined" sx={{ ml: 'auto', fontWeight: 600 }} />
                    </Box>

                    {/* DATE RANGE / DATE */}
                    {visitType === 'support' ? (
                        <>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                📅 Date
                            </Typography>
                            <Box sx={{ mt: 0.5, mb: 2 }}>
                                <TextField
                                    label="Date" type="date" size="small" fullWidth
                                    value={fromDate}
                                    onChange={e => {
                                        setFromDate(e.target.value);
                                        setToDate(e.target.value);
                                    }}
                                    inputProps={{ min: today }}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>
                        </>
                    ) : (
                        <>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                📅 Date Range
                            </Typography>
                            <Grid container spacing={1.5} sx={{ mt: 0.5, mb: 2 }}>
                                <Grid item xs={6}>
                                    <TextField
                                        label="From Date" type="date" size="small" fullWidth
                                        value={fromDate}
                                        onChange={e => setFromDate(e.target.value)}
                                        inputProps={{ min: today }}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="To Date" type="date" size="small" fullWidth
                                        value={toDate}
                                        onChange={e => setToDate(e.target.value)}
                                        inputProps={{ min: fromDate || today }}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}

                    {/* SALESPERSON — admin can pick anyone; sales locks to logged-in user */}
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        👤 Sales Person (Users)
                    </Typography>
                    {isAdmin ? (
                        <Autocomplete
                            sx={{ mt: 0.5, mb: 2 }}
                            size="small"
                            options={salesPersonList}
                            getOptionLabel={u => {
                                if (!u) return '';
                                const name = u.name || u.username || '';
                                const email = u.email ? ` (${u.email})` : '';
                                return name ? `${name}${email}` : (u.email || String(u.id));
                            }}
                            isOptionEqualToValue={(a, b) => a?.id === b?.id}
                            value={salesPersonList.find(u => u.id === salesPerson) || null}
                            onChange={(_, v) => setSalesPerson(v ? v.id : '')}
                            renderInput={params => (
                                <TextField {...params} label="Select User" placeholder="Search users..."
                                    InputProps={{ ...params.InputProps, startAdornment: <><PersonIcon sx={{ mr: 0.5, color: '#aaa', fontSize: 18 }} />{params.InputProps.startAdornment}</> }}
                                />
                            )}
                        />
                    ) : (
                        <TextField
                            sx={{ mt: 0.5, mb: 2 }}
                            size="small"
                            fullWidth
                            disabled
                            value={(() => {
                                const u = salesPersonList.find(x => x.id === salesPerson);
                                if (!u) return 'You (logged-in user)';
                                const name = u.name || u.username || '';
                                return u.email ? `${name} (${u.email})` : name;
                            })()}
                            InputProps={{
                                startAdornment: <PersonIcon sx={{ mr: 0.5, color: '#aaa', fontSize: 18 }} />,
                            }}
                            helperText="Visits are assigned to you automatically"
                        />
                    )}

                    {/* VISIT TYPE */}
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        🗂️ Visit Type
                    </Typography>
                    <ToggleButtonGroup
                        exclusive
                        fullWidth
                        size="small"
                        value={visitType}
                        onChange={(_, v) => {
                            if (v) {
                                setVisitType(v);
                                if (v === 'support' && fromDate) {
                                    setToDate(fromDate);
                                }
                            }
                        }}
                        sx={{
                            mt: 0.5,
                            mb: 2,
                            flexWrap: isMobileLayout ? 'wrap' : 'nowrap',
                            '& .MuiToggleButton-root': {
                                flex: isMobileLayout ? '1 1 30%' : undefined,
                                px: isMobileLayout ? 0.5 : undefined,
                            },
                        }}
                    >
                        {VISIT_TYPES.map((t) => (
                            <ToggleButton key={t.value} value={t.value} sx={{ textTransform: 'none', fontWeight: 600, fontSize: isMobileLayout ? 11 : 12 }}>
                                {t.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    {/* AVG PER DAY — sales and sales & support (hidden for support) */}
                    {(visitType === 'sales' || isSalesAndSupport(visitType)) && (
                        <>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                {visitType === 'sales'
                                    ? '🏫 Avg Schools per Day'
                                    : '🏫 Avg Our Institutes per Day'}
                            </Typography>
                            <TextField
                                sx={{ mt: 0.5, mb: 2 }}
                                size="small" fullWidth type="number"
                                value={avgPerDay}
                                error={Boolean(avgError)}
                                onChange={e => setAvgPerDay(e.target.value)}
                                inputProps={{
                                    min: visitType === 'sales' ? 6 : 1,
                                    max: isSalesAndSupport(visitType) ? MAX_SELECT : 20,
                                    step: 1,
                                }}
                                placeholder="6"
                                helperText={
                                    avgError
                                    || (isSalesAndSupport(visitType)
                                        ? `Default 6. How many selected institutes per day (max ${MAX_SELECT}).`
                                        : 'Default 6. Entering less than 6 shows an error.')
                                }
                            />
                        </>
                    )}

                    {/* SUPPORT / SALES+SUPPORT: Our Client locations */}
                    {includesSupport(visitType) && (
                        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: '1px solid #c8e6c9', bgcolor: '#f1f8f4' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <BusinessIcon sx={{ color: '#2e7d32', fontSize: 18 }} />
                                    <Typography variant="caption" fontWeight="bold" color="success.dark" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                                        {visitType === 'support' ? 'Company Institutes' : 'Our Institutes (Clients)'}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {visitType === 'support' && filteredClientLocations.length > 0 && (
                                        <Button
                                            size="small"
                                            sx={{ fontSize: 11, py: 0, px: 0.8, minWidth: 0, textTransform: 'none' }}
                                            onClick={() => {
                                                if (selectedStops.length === filteredClientLocations.length) {
                                                    setSelectedStops([]);
                                                } else {
                                                    setSelectedStops([...filteredClientLocations]);
                                                }
                                            }}
                                        >
                                            {selectedStops.length === filteredClientLocations.length ? 'Clear all' : 'Select all'}
                                        </Button>
                                    )}
                                    <Chip
                                        size="small"
                                        label={
                                            visitType === 'support'
                                                ? `${selectedStops.length} selected`
                                                : `${selectedStops.length}/${MAX_SELECT} selected`
                                        }
                                        color={
                                            isSalesAndSupport(visitType) && selectedStops.length >= MAX_SELECT
                                                ? 'warning'
                                                : 'success'
                                        }
                                        sx={{ fontWeight: 700 }}
                                    />
                                </Box>
                            </Box>
                            {visitType === 'support' && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Select the institutes to visit (select more or less).
                                </Typography>
                            )}
                            {isSalesAndSupport(visitType) && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Select our schools and other schools (max {MAX_SELECT}). Our schools get priority when planning.
                                </Typography>
                            )}
                            <TextField
                                size="small" fullWidth
                                placeholder={visitType === 'support' ? 'Filter company institutes...' : 'Filter our institutes...'}
                                value={clientFilter}
                                onChange={(e) => setClientFilter(e.target.value)}
                                sx={{ mb: 1 }}
                                InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, color: '#aaa', fontSize: 18 }} /> }}
                            />
                            {loadingClients ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <CircularProgress size={22} />
                                </Box>
                            ) : filteredClientLocations.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">
                                    {visitType === 'support'
                                        ? 'No active institutes in the company list.'
                                        : 'No institutions marked as Our Client yet. Mark them in All Schools → School/College Sync.'}
                                </Typography>
                            ) : (
                                <Box sx={{ maxHeight: 180, overflowY: 'auto', mb: 1 }}>
                                    {filteredClientLocations.map((client) => {
                                        const checked = isStopSelected(client);
                                        const disableAdd = isSalesAndSupport(visitType) && !checked && selectedStops.length >= MAX_SELECT;
                                        return (
                                            <Box
                                                key={stopKey(client)}
                                                onClick={() => !disableAdd && toggleStopSelection(client)}
                                                sx={{
                                                    display: 'flex', alignItems: 'flex-start', gap: 0.5,
                                                    py: 0.6, px: 0.5, borderRadius: 1, cursor: disableAdd ? 'not-allowed' : 'pointer',
                                                    opacity: disableAdd ? 0.45 : 1,
                                                    bgcolor: checked ? '#e8f5e9' : 'transparent',
                                                    '&:hover': { bgcolor: disableAdd ? 'transparent' : '#eef7f0' },
                                                }}
                                            >
                                                <Checkbox size="small" checked={checked} disabled={disableAdd} sx={{ p: 0.3, mt: 0.1 }} />
                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Typography variant="body2" fontWeight={600} noWrap>{client.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                                                        {client.address || 'No address'}
                                                    </Typography>
                                                    <VisitHistoryLine school={client} />
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}

                            {/* Sales & Support only: mix with other schools via area search below */}
                            {isSalesAndSupport(visitType) && selectedStops.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                    {prioritizeOurSchools(selectedStops).map((s) => (
                                        <Chip
                                            key={stopKey(s)}
                                            size="small"
                                            label={s.is_client ? `${s.name} ★` : s.name}
                                            color={s.is_client ? 'success' : 'primary'}
                                            onDelete={() => toggleStopSelection(s)}
                                            sx={{ maxWidth: '100%' }}
                                        />
                                    ))}
                                </Box>
                            )}
                            {visitType === 'support' && selectedStops.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                    {selectedStops.map((s) => (
                                        <Chip
                                            key={stopKey(s)}
                                            size="small"
                                            label={s.name}
                                            color="success"
                                            onDelete={() => toggleStopSelection(s)}
                                            sx={{ maxWidth: '100%' }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                    <Divider sx={{ my: 1.5 }} />

                    {/* AREA SEARCH — sales & sales+support only (support = our institutes only) */}
                    {(visitType === 'sales' || isSalesAndSupport(visitType)) && (
                        <>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        📍 Area & Radius{isSalesAndSupport(visitType) ? ' (other schools)' : ''}
                    </Typography>

                    <Grid container spacing={1.5} sx={{ mb: 1, mt: 0.5 }}>
                        <Grid item xs={8}>
                            <GoogleInstituteSearch
                                open={visitType === 'sales' || isSalesAndSupport(visitType)}
                                getOrigin={() => locationPin || areaCentre || startLoc}
                                onPlace={(place) => {
                                    const lat = Number(place.lat);
                                    const lng = Number(place.lng);
                                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
                                    const label = place.address || place.name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
                                    const point = { lat, lng };
                                    setAreaInput(label);
                                    setInputValue(label);
                                    setAreaCoords(point);
                                    setLocationPin(point);
                                    setAreaCentre(point);
                                    setFocusTarget({ lat, lng, token: Date.now() });
                                    setError('');
                                    setPinMode('');
                                    setStatusMsg(`Location pin moved to ${label}.`);
                                    if (plannerView === 'map') {
                                        searchSchoolsRef.current?.({ area: label, coords: point });
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                select
                                label="Radius"
                                size="small" fullWidth
                                value={radiusKm}
                                onChange={e => setRadiusKm(Number(e.target.value))}
                            >
                                {RADIUS_OPTIONS.map(km => (
                                    <MenuItem key={km} value={km}>{km} km</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 1.5 }}>
                        {RADIUS_OPTIONS.map(km => (
                            <Chip
                                key={km}
                                size="small"
                                label={`${km} km`}
                                color={radiusKm === km ? 'primary' : 'default'}
                                variant={radiusKm === km ? 'filled' : 'outlined'}
                                onClick={() => setRadiusKm(km)}
                                sx={{ fontWeight: 600 }}
                            />
                        ))}
                    </Box>

                    <Button
                        variant="outlined" color='secondary' fullWidth size="small"
                        startIcon={locatingGPS ? <CircularProgress size={16} color="inherit" /> : <MyLocationIcon />}
                        disabled={locatingGPS || searching}
                        onClick={async () => {
                            if (!navigator.geolocation) {
                                setError('Sorry, your browser does not support geolocation.');
                                return;
                            }
                            setLocatingGPS(true);
                            setStatusMsg('📡 Getting your current location...');
                            navigator.geolocation.getCurrentPosition(
                                async (pos) => {
                                    const { latitude, longitude } = pos.coords;
                                    setStartLoc({ lat: latitude, lng: longitude });
                                    setIsGPSLocation(true);
                                    const here = { lat: latitude, lng: longitude };
                                    setLocationPin(here);
                                    setAreaCentre(here);
                                    setAreaCoords(here);
                                    try {
                                        const place = await reverseGeocode(latitude, longitude);
                                        const locality = place.locality || '';
                                        if (locality) {
                                            setAreaInput(locality);
                                            setInputValue(locality);
                                            setStatusMsg(`📍 Location found: ${locality}. Click "Find Schools" to search.`);
                                        } else {
                                            setAreaInput(`${latitude.toFixed(5)}, ${longitude.toFixed(5)}`);
                                            setInputValue(`${latitude.toFixed(5)}, ${longitude.toFixed(5)}`);
                                            setStatusMsg('📍 Location detected. Click "Find Schools" to search.');
                                        }
                                    } catch {
                                        setAreaInput(`${latitude.toFixed(5)}, ${longitude.toFixed(5)}`);
                                        setInputValue(`${latitude.toFixed(5)}, ${longitude.toFixed(5)}`);
                                        setStatusMsg('📍 Location detected. Click "Find Schools" to search.');
                                    }
                                    setLocatingGPS(false);
                                },
                                () => {
                                    setError('Could not get your location. Please ensure GPS is enabled.');
                                    setLocatingGPS(false);
                                },
                                {
                                    enableHighAccuraccy: true, timeout: 8000
                                }
                            );

                        }}
                        sx={{ mb: 1.5, borderRadius: 2, fontWeight: 600 }}
                    >
                        {/* {searching ? 'Searching...' : `Find Schools and Colleges in ${radiusKm} km`} */}
                        {locatingGPS ? 'Detecting location...' : 'Use my current location'}
                    </Button>
                    <Button variant='contained' fullWidth size='small'
                        startIcon={searching ? <CircularProgress size={16} color='inherit' /> : <SearchIcon />}
                        onClick={handleSearchSchools}
                        disabled={searching || (!areaInput.trim() && !locationPin)}
                        sx={{ mb: 1.5, borderRadius: 2, fontWeight: 600 }}
                    >
                        {searching
                            ? (searchProgress?.total > 1
                                ? `Searching ${Math.min(searchProgress.done + 1, searchProgress.total)}/${searchProgress.total}...`
                                : 'Searching nearby...')
                            : `Find private & aided schools within ${radiusKm} km`}
                    </Button>

                    {foundSchools.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                            <Chip size="small" icon={<SchoolIcon />} label={`${foundSchools.filter(s => s.type === 'school').length} Schools`} color="primary" variant="outlined" />
                            <Chip size="small" icon={<SchoolIcon />} label={`${foundSchools.filter(s => s.type === 'college').length} Colleges`} color="secondary" variant="outlined" />
                            <Chip size="small" label={`${foundSchools.length} Total`} color="success" variant="filled" />
                            {foundSchools.some(isNotInterestedSchool) && (
                                <Chip
                                    size="small"
                                    label={`🚫 ${foundSchools.filter(isNotInterestedSchool).length} Not interested`}
                                    sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 600 }}
                                />
                            )}
                        </Box>
                    )}

                    {/* Sales & Support only: pick other schools from search */}
                    {isSalesAndSupport(visitType) && foundSchools.length > 0 && (
                        <Box sx={{ mb: 1.5, maxHeight: 160, overflowY: 'auto', border: '1px solid #dde6ff', borderRadius: 1.5, p: 1 }}>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Select other schools to add (max {MAX_SELECT} total — our schools planned first)
                            </Typography>
                            {foundSchools.filter((s) => !isNotInterestedSchool(s)).map((school) => {
                                const checked = isStopSelected(school);
                                const disableAdd = !checked && selectedStops.length >= MAX_SELECT;
                                return (
                                    <Box
                                        key={stopKey(school)}
                                        onClick={() => !disableAdd && toggleStopSelection({ ...school, is_client: false })}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 0.5,
                                            py: 0.4, cursor: disableAdd ? 'not-allowed' : 'pointer',
                                            opacity: disableAdd ? 0.45 : 1,
                                            bgcolor: checked ? '#e3f2fd' : 'transparent',
                                            borderRadius: 1, px: 0.5,
                                        }}
                                    >
                                        <Checkbox size="small" checked={checked} disabled={disableAdd} sx={{ p: 0.2 }} />
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography variant="body2" noWrap>{school.name}</Typography>
                                            <VisitHistoryLine school={school} />
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                        </>
                    )}

                    {/* GENERATE / PLAN SCHEDULE */}
                    <Button
                        variant="outlined"
                        fullWidth
                        size="medium"
                        startIcon={<AddBusinessIcon />}
                        onClick={() => setAddInstituteOpen(true)}
                        sx={{ fontWeight: 700, borderRadius: 2, textTransform: 'none', mb: 1 }}
                    >
                        Add new institute
                    </Button>
                    {visitType === 'support' ? (
                        <Button
                            variant="contained"
                            color="success"
                            fullWidth
                            size="large"
                            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                            onClick={handleSaveSupportPlan}
                            disabled={saving || !salesPerson || !fromDate || selectedStops.length === 0}
                            sx={{ fontWeight: 'bold', borderRadius: 2, mb: 1 }}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="contained" fullWidth size="large"
                                startIcon={planning ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                                onClick={handleGeneratePlan}
                                disabled={
                                    planning
                                    || Boolean(avgError)
                                    || !fromDate
                                    || !toDate
                                    || (isSalesAndSupport(visitType)
                                        ? selectedStops.length === 0
                                        : foundSchools.length === 0)
                                }
                                sx={{ fontWeight: 'bold', borderRadius: 2, background: 'linear-gradient(90deg,#4680FF,#1665D8)', mb: 1 }}
                            >
                                {planning
                                    ? 'Planning...'
                                    : isSalesAndSupport(visitType)
                                        ? `Plan Schedule (${selectedStops.length}/${MAX_SELECT}) — our schools first`
                                        : 'Generate Day-wise Plan'}
                            </Button>

                            {/* SAVE */}
                            {plannedDays.length > 0 && (
                                <Button
                                    variant="contained" color="success" fullWidth size="large"
                                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                                    onClick={handleSavePlan}
                                    disabled={saving || !salesPerson}
                                    sx={{ fontWeight: 'bold', borderRadius: 2 }}
                                >
                                    {saving ? 'Saving...' : 'Confirm & Save Route'}
                                </Button>
                            )}
                        </>
                    )}

                    {statusMsg && (
                        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1, fontWeight: 500 }}>
                            ⏳ {statusMsg}
                        </Typography>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mt: 1, py: 0 }} onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}
                </Paper>

                {/* ── DAY ITINERARY ── */}
                {plannedDays.length > 0 && (
                    <Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column', bgcolor: 'transparent', flexShrink: 0 }}>

                        <Tabs
                            value={selectedDate}
                            onChange={(_, v) => setSelectedDate(v)}
                            variant="scrollable" scrollButtons="auto"
                            sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 42, mb: 1 }}
                            TabIndicatorProps={{ style: { background: '#4680FF' } }}
                        >
                            {plannedDays.map((d, i) => (
                                <Tab
                                    key={d.date} value={d.date}
                                    label={
                                        <Badge badgeContent={d.route.length} color="primary" max={99} sx={{ '& .MuiBadge-badge': { fontSize: 9, minWidth: 16, height: 16 } }}>
                                            <Typography variant="caption" fontWeight={600} sx={{ pr: 1 }}>
                                                Day {i + 1}<br />
                                                <span style={{ fontWeight: 400, fontSize: 10 }}>{fmt(d.date)}</span>
                                            </Typography>
                                        </Badge>
                                    }
                                    sx={{ minWidth: 72, px: 1.5, py: 1 }}
                                />
                            ))}
                        </Tabs>

                        <Box sx={{ pb: 2 }}>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Search schools in this day"
                                value={dayPlanSearch}
                                onChange={(e) => setDayPlanSearch(e.target.value)}
                                sx={{ mb: 1.2 }}
                                InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, color: '#888', fontSize: 18 }} /> }}
                            />
                            {currentRoute.length > 0 && visibleDayStops.length === 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    No schools on this day match that search.
                                </Typography>
                            )}
                            {visibleDayStops.map(({ school, idx }) => (
                                <Box
                                    key={idx} draggable
                                    onDragStart={() => { dragItem.current = idx; }}
                                    onDragEnter={() => { dragOverItem.current = idx; }}
                                    onDragEnd={handleDrop}
                                    onDragOver={e => e.preventDefault()}
                                    sx={{
                                        p: 1.5, mb: 1.2, borderRadius: 2,
                                        display: 'flex', flexDirection: 'column',
                                        bgcolor: '#e6e8ec', // Matches the grey iOS style card
                                        border: '1px solid #d1d3d8',
                                        cursor: 'grab', '&:active': { cursor: 'grabbing' },
                                        transition: 'background-color 0.2s',
                                        '&:hover': { bgcolor: '#e0e2e6' }
                                    }}
                                >
                                    {/* Top Row: Icon, Time, Scheduled Badge, Icons */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <DragIndicatorIcon sx={{ color: '#555', fontSize: 18 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                                {generateTime(idx)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip
                                                label="Scheduled"
                                                size="small"
                                                sx={{
                                                    bgcolor: '#cbebd8',
                                                    color: '#1a6b3d',
                                                    fontWeight: 600,
                                                    height: 20,
                                                    fontSize: '0.65rem',
                                                    borderRadius: 1
                                                }}
                                            />
                                            <AccessTimeIcon sx={{ color: '#555', fontSize: 18 }} />
                                            <Tooltip title="Block school — hide it from the planner">
                                                <IconButton
                                                    size="small"
                                                    disabled={blockingKey === stopKey(school)}
                                                    onClick={() => openBlockDialog(school)}
                                                    sx={{ p: 0 }}
                                                >
                                                    {blockingKey === stopKey(school)
                                                        ? <CircularProgress size={16} />
                                                        : <BlockOutlinedIcon fontSize="small" sx={{ color: '#b45309' }} />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Remove stop">
                                                <IconButton size="small" onClick={() => deleteStop(activeDayIndex, idx)} sx={{ p: 0 }}>
                                                    <DeleteOutlineIcon fontSize="small" sx={{ color: '#d32f2f' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    {/* School Name */}
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1.5, pl: 0.5, pr: 0.5 }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.2, color: '#111' }}>
                                                {school.name}
                                            </Typography>
                                            <VisitHistoryLine school={school} />
                                        </Box>
                                        {school.is_client && (
                                            <Chip
                                                size="small"
                                                label="Our School"
                                                color="success"
                                                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                                            />
                                        )}
                                    </Box>

                                    {/* Bottom Row: Distance & Area */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <LocationOnOutlinedIcon sx={{ fontSize: 16, color: '#555' }} />
                                            <Typography variant="caption" sx={{ color: '#333', fontWeight: 500 }}>
                                                {school.distance_km != null ? `${school.distance_km} km` : `${(Math.random() * 8 + 1).toFixed(1)} km`}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <LocationOnOutlinedIcon sx={{ fontSize: 16, color: '#555' }} />
                                            <Typography variant="caption" sx={{ color: '#333', fontWeight: 500 }}>
                                                {school.area || school.address || areaInput || 'Bengaluru'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            ))}

                            {/* Add schools to this day after plan is ready */}
                            <Box sx={{ mt: 0.5, mb: 1 }}>
                                <Button
                                    size="small"
                                    fullWidth
                                    variant={showAddToDay ? 'contained' : 'outlined'}
                                    color="secondary"
                                    startIcon={<SchoolIcon />}
                                    onClick={() => setShowAddToDay((v) => !v)}
                                    sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}
                                >
                                    {showAddToDay
                                        ? (visitType === 'support' ? 'Hide add institutes' : 'Hide add schools')
                                        : (visitType === 'support' ? 'Add our institutes to this day' : 'Add schools to this day')}
                                </Button>

                                {showAddToDay && (
                                    <Box sx={{ mt: 1, p: 1.2, borderRadius: 2, border: '1px solid #dde6ff', bgcolor: '#f8faff' }}>
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                                            Add to {selectedDate ? fmt(selectedDate) : 'selected day'}
                                            {visitType === 'support'
                                                ? ' — our institutes only'
                                                : includesSupport(visitType) ? ' — our schools listed first' : ''}
                                        </Typography>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Search schools to add"
                                            value={addToDaySearch}
                                            onChange={(e) => setAddToDaySearch(e.target.value)}
                                            sx={{ mb: 1, bgcolor: '#fff' }}
                                            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, color: '#888', fontSize: 18 }} /> }}
                                        />

                                        {visibleAddableSchools.length === 0 ? (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                {addToDaySearch.trim()
                                                    ? 'No schools match this search.'
                                                    : visitType === 'support'
                                                    ? 'All our institutes are already on the plan.'
                                                    : 'No more schools available. Use area search above to find more, then add them here.'}
                                            </Typography>
                                        ) : (
                                            <Box sx={{ maxHeight: 180, overflowY: 'auto', mb: 0.5 }}>
                                                {visibleAddableSchools.map((school) => (
                                                    <Box
                                                        key={stopKey(school)}
                                                        onClick={() => addStopToDay(school)}
                                                        sx={{
                                                            display: 'flex', alignItems: 'center', gap: 0.5,
                                                            py: 0.55, px: 0.5, borderRadius: 1, cursor: 'pointer',
                                                            '&:hover': { bgcolor: school.is_client ? '#e8f5e9' : '#e3f2fd' },
                                                        }}
                                                    >
                                                        <SchoolIcon sx={{ fontSize: 16, color: school.is_client ? '#2e7d32' : '#1976d2' }} />
                                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                                            <Typography variant="body2" fontWeight={600} noWrap>
                                                                {school.name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                                {school.is_client ? 'Our institute' : (school.address || school.type || 'School')}
                                                            </Typography>
                                                            <VisitHistoryLine school={school} />
                                                        </Box>
                                                        <Chip
                                                            size="small"
                                                            label="Add"
                                                            color={school.is_client ? 'success' : 'primary'}
                                                            variant="outlined"
                                                            sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700 }}
                                                        />
                                                        <Tooltip title="Block — do not show this school again">
                                                            <IconButton
                                                                size="small"
                                                                disabled={blockingKey === stopKey(school)}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openBlockDialog(school);
                                                                }}
                                                            >
                                                                <BlockOutlinedIcon sx={{ fontSize: 16, color: '#b45309' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Paper>
                )}
            </Box>

            {/* ── RIGHT: MAP ── */}
            <Paper
                elevation={3}
                sx={{
                    flexGrow: 1,
                    overflow: 'hidden',
                    borderRadius: 2,
                    position: 'relative',
                    display: plannerView === 'map' || !isMobileLayout || mobileTab === 1 ? 'block' : 'none',
                    minHeight: 0,
                    height: '100%',
                }}
            >

                {plannerView === 'map' ? (
                    <>
                    <GooglePlanMap
                        center={locationPin || areaCentre || startLoc}
                        schools={mapViewSchools}
                        plannedStopKeys={mapSelectionKeys}
                        route={mapSelection}
                        startLoc={startLoc}
                        onStartChange={(point) => {
                            setStartLoc(point);
                            setIsGPSLocation(false);
                        }}
                        busy={searching || savingMapSchool || saving}
                        onPlaceSelected={handleGooglePlace}
                        onAddToPlan={addSchoolFromMap}
                        onCreateSchool={saveMapPickedSchool}
                        onLocate={handlePlanMapLocate}
                    />
                    {mapSelection.length > 0 && (
                        <Button
                            variant="contained"
                            onClick={() => {
                                const today = localToday();
                                setMapFromDate(today);
                                setMapToDate(today);
                                setMapPlanOpen(true);
                            }}
                            sx={{
                                position: 'absolute',
                                zIndex: 3,
                                left: '50%',
                                bottom: 24,
                                transform: 'translateX(-50%)',
                                textTransform: 'none',
                                fontWeight: 700,
                                borderRadius: 8,
                                px: 2.5,
                            }}
                        >
                            {visitType === 'support' ? `Save plan (${mapSelection.length})` : `Create plan (${mapSelection.length})`}
                        </Button>
                    )}
                    </>
                ) : (
                <>
                <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-end', maxWidth: 'calc(100% - 24px)' }}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={pinMode || null}
                        onChange={(_, value) => setPinMode(value || '')}
                        sx={{ bgcolor: 'rgba(255,255,255,0.96)', boxShadow: 2 }}
                    >
                        <ToggleButton value="start" sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, px: 1.2 }}>
                            Start pin
                        </ToggleButton>
                        <ToggleButton value="location" sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, px: 1.2 }}>
                            Location pin
                        </ToggleButton>
                    </ToggleButtonGroup>
                    {pinMode && (
                        <Typography variant="caption" sx={{ bgcolor: 'rgba(255,255,255,0.96)', px: 1, py: 0.4, borderRadius: 1, boxShadow: 1, fontWeight: 600 }}>
                            {pinMode === 'start' ? 'Click the map to place the start pin' : 'Click the map to choose a location'}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 0.5, bgcolor: 'rgba(255,255,255,0.96)', p: 0.5, borderRadius: 2, boxShadow: 2, alignItems: 'center' }}>
                        <Tooltip title="Move start pin to the location pin">
                            <IconButton
                                size="small"
                                onClick={() => {
                                    const target = locationPin || areaCentre || areaCoords;
                                    if (!target?.lat || !target?.lng) {
                                        setError('Drop a location pin or search an area first.');
                                        return;
                                    }
                                    setStartLoc({ lat: target.lat, lng: target.lng });
                                    setIsGPSLocation(false);
                                    setPinMode('');
                                    setError('');
                                    setStatusMsg('Start pin moved to the location pin.');
                                }}
                            >
                                <PushPinIcon sx={{ color: '#1b5e20' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Show start pin">
                            <IconButton
                                size="small"
                                onClick={() => {
                                    if (startLoc?.lat == null) return;
                                    setFocusTarget({ lat: startLoc.lat, lng: startLoc.lng, token: Date.now() });
                                    setStatusMsg('Showing the start pin.');
                                }}
                            >
                                <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#1b5e20', lineHeight: 1 }}>S</Typography>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Use current location for the start pin">
                            <IconButton size="small" onClick={handleUseCurrentLocation} disabled={locatingGPS}>
                                {locatingGPS ? <CircularProgress size={16} /> : <MyLocationIcon color="primary" />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Typography variant="caption" sx={{ bgcolor: 'rgba(255,255,255,0.92)', px: 1, py: 0.3, borderRadius: 1, boxShadow: 1 }}>
                        <Box component="span" sx={{ color: '#1b5e20', fontWeight: 800 }}>S</Box> start
                        {' · '}
                        <Box component="span" sx={{ color: '#e65100', fontWeight: 800 }}>L</Box> location
                    </Typography>
                </Box>

                {plannedDays.length > 0 && selectedDate && (
                    <Box sx={{ position: 'absolute', top: 10, left: 10, zIndex: 1000, bgcolor: 'rgba(255,255,255,0.92)', borderRadius: 1.5, px: 1.5, py: 0.8, boxShadow: 2 }}>
                        <Typography variant="caption" fontWeight="bold" color="primary">
                            📅 {fmt(selectedDate)} — {currentRoute.length} stops
                        </Typography>
                    </Box>
                )}
                <RouteMap
                    route={currentRoute}
                    startLoc={startLoc}
                    areaCentre={areaCentre}
                    locationPin={locationPin}
                    setLocationPin={handleLocationPinChange}
                    setStartLoc={(loc) => {
                        setStartLoc(loc);
                        setIsGPSLocation(false);
                        if (pinMode === 'start') {
                            setPinMode('');
                            setStatusMsg(`Start pin set (${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)}).`);
                        }
                    }}
                    foundSchools={mapSchools}
                    radiusKm={radiusKm}
                    pinMode={pinMode}
                    focusTarget={focusTarget}
                    resizeKey={`${isMobileLayout ? 'm' : 'd'}-${mobileTab}-${showPhoneFrame ? 'frame' : 'full'}`}
                />
                </>
                )}
            </Paper>
            </Box>

            </Box>

            <Dialog
                open={mapPlanOpen}
                onClose={() => { if (!saving) setMapPlanOpen(false); }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {visitType === 'support' ? 'Save visits' : 'Create plan'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        {mapSelection.length} selected school{mapSelection.length === 1 ? '' : 's'} will be saved as visits {visitType === 'support' ? 'on this date.' : 'across this date range.'}
                    </Typography>
                    {visitType === 'support' ? (
                        <TextField
                            label="Date"
                            type="date"
                            size="small"
                            fullWidth
                            value={mapFromDate}
                            onChange={(e) => {
                                setMapFromDate(e.target.value);
                                setMapToDate(e.target.value);
                            }}
                            inputProps={{ min: today }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ mb: 1.5 }}
                        />
                    ) : (
                        <>
                            <TextField
                                label="From date"
                                type="date"
                                size="small"
                                fullWidth
                                value={mapFromDate}
                                onChange={(e) => setMapFromDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ mb: 1.5 }}
                            />
                            <TextField
                                label="To date"
                                type="date"
                                size="small"
                                fullWidth
                                value={mapToDate}
                                onChange={(e) => setMapToDate(e.target.value)}
                                inputProps={{ min: mapFromDate }}
                                InputLabelProps={{ shrink: true }}
                            />
                        </>
                    )}
                    {!salesPerson && (
                        <Alert severity="warning" sx={{ mt: 1.5 }}>
                            Select a sales person on the Plan tab before submitting.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setMapPlanOpen(false)} disabled={saving} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={submitMapPlan}
                        disabled={saving || !mapFromDate || (visitType !== 'support' && !mapToDate) || !salesPerson}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(blockTarget)}
                onClose={() => { if (!blockingKey) { setBlockTarget(null); setBlockReason(''); } }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Block institute</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        {blockTarget?.name || 'This institute'} will be hidden from Visit Planner.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                        label="Reason"
                        placeholder="Why is this institute blocked?"
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => { setBlockTarget(null); setBlockReason(''); }}
                        disabled={Boolean(blockingKey)}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={blockSchool}
                        disabled={Boolean(blockingKey) || !blockReason.trim()}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                        {blockingKey ? 'Blocking...' : 'Block'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={addInstituteOpen}
                onClose={() => { if (!savingInstitute) setAddInstituteOpen(false); }}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Add new institute</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Institute name"
                        fullWidth
                        size="small"
                        value={newInstName}
                        onChange={(e) => setNewInstName(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                    <TextField
                        select
                        margin="dense"
                        label="Type"
                        fullWidth
                        size="small"
                        value={newInstType}
                        onChange={(e) => setNewInstType(e.target.value)}
                    >
                        <MenuItem value="school">School</MenuItem>
                        <MenuItem value="college">College</MenuItem>
                    </TextField>
                    <TextField
                        margin="dense"
                        label="Google Maps share link"
                        fullWidth
                        size="small"
                        placeholder="Paste maps.app.goo.gl or a google.com/maps link"
                        value={mapShareLink}
                        error={Boolean(mapLinkError)}
                        helperText={mapLinkError || 'Paste a share link, then search to place the pin.'}
                        onChange={(e) => {
                            setMapShareLink(e.target.value);
                            if (mapLinkError) setMapLinkError('');
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                searchByMapShareLink();
                            }
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Button
                                        size="small"
                                        onClick={searchByMapShareLink}
                                        disabled={resolvingMapLink || !mapShareLink.trim()}
                                        sx={{ textTransform: 'none', fontWeight: 700, minWidth: 72 }}
                                    >
                                        {resolvingMapLink ? 'Finding...' : 'Search'}
                                    </Button>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <style>{`.pac-container{z-index:2000 !important;}`}</style>
                    <GoogleInstituteSearch
                        open={addInstituteOpen}
                        getOrigin={() => newInstLoc || locationPin || areaCentre || startLoc}
                        onPlace={(place) => {
                            setNewInstLoc({
                                lat: place.lat,
                                lng: place.lng,
                                address: place.address || '',
                            });
                            if (!newInstAddress.trim() && place.address) setNewInstAddress(place.address);
                            if (!newInstName.trim() && place.name) setNewInstName(place.name);
                        }}
                    />
                    <InstitutePickMap
                        open={addInstituteOpen}
                        location={newInstLoc}
                        fallback={locationPin || startLoc}
                        onPick={pickInstituteLocation}
                    />
                    {newInstLoc?.lat != null && (
                        <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.75 }}>
                            Location selected: {Number(newInstLoc.lat).toFixed(5)}, {Number(newInstLoc.lng).toFixed(5)}
                        </Typography>
                    )}
                    <TextField
                        margin="dense"
                        label="Address"
                        fullWidth
                        size="small"
                        value={newInstAddress}
                        onChange={(e) => setNewInstAddress(e.target.value)}
                    />
                    <Button
                        size="small"
                        sx={{ mt: 0.5, textTransform: 'none' }}
                        disabled={!locationPin?.lat}
                        onClick={() => {
                            if (!locationPin?.lat) return;
                            setNewInstLoc({
                                lat: locationPin.lat,
                                lng: locationPin.lng,
                                address: areaInput || newInstAddress,
                            });
                            if (!newInstAddress.trim() && areaInput) setNewInstAddress(areaInput);
                        }}
                    >
                        Use the location pin on the map
                    </Button>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setAddInstituteOpen(false)}
                        disabled={savingInstitute}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={saveNewInstitute}
                        disabled={savingInstitute || !newInstName.trim() || newInstLoc?.lat == null}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                        {savingInstitute ? 'Saving...' : 'Save and show on map'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VisitPlanner;
